import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useIntroNote } from '@/src/renderer/lib/intro'
import { PageHelpButton } from '@/src/renderer/components/common/PageHelpButton'
import { useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDataSource } from '../../../../lib/dataSource'
import { usePlayTask } from '../../../../lib/focus'
import { useTabActions } from '../../../../contexts/TabManagerContext'
import type { Tab } from '../../../../hooks/multiWindow'
import { useBreadcrumbTitle } from '../../../../contexts/BreadcrumbTitleContext'
import { useCanvasMode } from '@/src/renderer/contexts/CanvasFocusContext'
import { useScrollbarVisibility } from '@/src/renderer/hooks/useScrollbarVisibility'
import { dispatchAppEvent, onAppEvent } from '../../../../lib/events'
import type { LifeSpace, Task, Section, UpdateProject } from '../../../../lib/db/models'
import { TitleRecognitionScope, type TaskCreationData } from '../../components/TaskEntry'
import type { TaskListHandle } from '../../components/TaskList'
import { TaskBoard } from '../../components/TaskBoard'
import { TaskStackView } from '../../components/TaskStackView'
import { useProjectNavigation } from '../../hooks/useProjectNavigation'
import { useStackModeSelection } from '../../hooks/useStackModeSelection'
import { useScrollFades } from '../../hooks/useScrollFades'
import { useTaskOperations } from '../../hooks/useTaskOperations'
import { useProjectSections } from '../../hooks/useProjectSections'
import { useApplyCanvasNotes } from '../../hooks/useApplyCanvasNotes'
import { useProjectHeader } from '../../hooks/useProjectHeader'
import { useHeaderActionsFit } from '../../hooks/useHeaderActionsFit'
import { useTaskCreation } from '../../hooks/useTaskCreation'
import { useTaskDragAndDrop } from '../../hooks/useTaskDragAndDrop'
import { useNestActions } from '../../hooks/useNestActions'
import { useNestMenu } from '../../hooks/useNestMenu'
import { useKeyboardMove } from '../../hooks/useKeyboardMove'
import { usePendingSelection } from '../../hooks/usePendingSelection'
import { useSectionDrag, type OptimisticSectionUpdates } from '../../hooks/useSectionDrag'
import { useSectionDragAnimation } from '../../hooks/useSectionDragAnimation'
import { useProjectKeyboardShortcuts } from '../../hooks/useProjectKeyboardShortcuts'
import { useDuplicateItems } from '../../hooks/useDuplicateItems'
import { useContextMenuMarks } from '../../hooks/useContextMenuMarks'
import { useTaskMenuShortcuts, anchorTaskId } from '../../hooks/useTaskMenuShortcuts'
import { useAppTaskMenu, menuAnchorOf, useShowTaskInProject } from '../../hooks/useAppTaskMenu'
import { MoveMenuContext } from '../../utils/moveMenu'
import { useMoveMenu } from '../../hooks/useMoveMenu'
import { useTaskUpdates } from '../../hooks/useTaskUpdates'
import { useSelectionActions } from '../../hooks/useSelectionActions'
import { useFavoriteShortcut, useProjectFavoriteShortcut } from '../../hooks/useFavoriteShortcut'
import type { TaskMenuTarget } from '../../components/TaskMenu/TaskMenu.types'
import { useContextMenuBadgeUpdate } from '../../hooks/useContextMenuBadgeUpdate'
import { useOptimisticTaskOverrides } from '../../hooks/useOptimisticTaskOverrides'
import { useOptimisticSectionOverrides } from '../../hooks/useOptimisticSectionOverrides'
import { useCompletionGrace } from '../../hooks/useCompletionGrace'
import { groupArchivedCompleted, isHiddenCompleted, splitCompleted } from '../../utils/completedGate'
import { isClosedByArchive } from '@/src/renderer/lib/db/taskFilters'
import { wakeTaskUpdate } from '../../utils/buildTaskUpdate'
import { useTaskDetailsPane, type UseTaskDetailsPane } from '../../hooks/useTaskDetailsPane'
import { TaskDetailsOpenerProvider } from '../../hooks/TaskDetailsOpener'
import { CompletedToggle } from '../../components/CompletedToggle'
import { FavoriteToggle } from '../../components/FavoriteToggle'
import { SectionHeader } from '../../components/SectionHeader'
import { collapsedSectionOf } from '../../utils/revealCollapsed'
import { whenTaskRowSettled } from '@/src/renderer/lib/taskJump/whenTaskRowSettled'
import { ArchiveSectionDialog } from '../../components/ArchiveSectionDialog'
import { useOptimisticOrder } from '../../hooks/useOptimisticOrder'
import { usePageCrossfade } from './hooks/usePageCrossfade'
import { useSelectionCache } from './hooks/useSelectionCache'
import { projectSelectionCache } from './hooks/projectSelectionCacheRegistry'
import { useDndComposition } from './hooks/useDndComposition'
import { CanvasEmptyStateTeaser, ProjectCanvasPanel } from '../../components/ProjectCanvas'
import { ListToolbarDock, LIST_TOOLBAR_CLEARANCE } from '../../components/ListToolbarDock'
import { resolveNewTaskTarget } from './utils/listToolbarTarget'
import { resolveNewSectionPlacement, type NewSectionWhere } from './utils/newSectionPlacement'
import { measureVisibleSectionKeys, LIST_TOP_FADE } from './utils/measureVisibleSections'
import { EmptyStateGlow } from '../../components/EmptyState'
import {
  useCanvasPanelShortcut,
  type CanvasShortcutTarget,
} from '../../components/ProjectCanvas/hooks/useCanvasPanelShortcut'
import type { CanvasPanelState } from '../../components/ProjectCanvas/ProjectCanvas.types'
import { useCanvasNotes } from '../../components/ProjectCanvas/hooks/useCanvasNotes'
import { shouldShowCanvasPeek } from '../../components/ProjectCanvas/utils/canvasPeek'
import { watchPlacementDrag } from '../../components/ProjectCanvas/utils/canvasPlacement'
import { LIST_MIN_WIDTH } from '../../components/ProjectCanvas/utils/canvasSplitDrag'
import type { CanvasPlacementRequest, CanvasRevealRequest } from '../../components/ProjectCanvas/ProjectCanvasPanel.types'
import { shouldShowCanvasEmptyState } from './utils/shouldShowCanvasEmptyState'
import { shouldRenderCanvasPanel } from './utils/shouldRenderCanvasPanel'
import { useUndoRegionTracking } from './hooks/useUndoRegionTracking'
import { nextCanvasPanelState } from './utils/nextCanvasPanelState'
import { isListToolbarRetracted } from './utils/isListToolbarRetracted'
import { useIsActiveTab, useTabId } from '../../../../pages/TabStack/ActiveTabContext'
import { useWindowMode } from '@/src/renderer/contexts/WindowModeProvider'
import { WindowModeToggle } from '../../../../components/common/WindowModeToggle'
import { TaskDetails, DetailsToggle } from '../../components/TaskDetails'
import { topVisibleTaskId } from '../../hooks/useTopVisibleTask'
import { consumePendingCanvasJump, consumePendingTaskJump } from '@/src/renderer/lib/taskJump/pendingTaskJump'
import { revealSubtask } from '../../utils/revealSubtask'
import { useNewTaskDrag } from '../../hooks/useNewTaskDrag'
import { applyNewTaskDrop, type NewTaskDropTarget } from '../../utils/newTaskDropTarget'
import { acceptsNewSection, sectionCreationForDropTarget } from '../../utils/newSectionDrop'
import { createLatestGate } from '../../utils/latestGate'
import { NewTaskDragGhost } from '../../components/NewTaskDrag'
import '../TaskViews.scss'
import './ProjectPage.scss'
import { ProjectHeaderTitle } from '../../components/ProjectTitle'

import type { RenderSegment } from './components/ProjectSegment'
import { getScrollContainer } from '@/src/renderer/lib/dom/getScrollContainer'
import { useWindowRevealHold } from '@/src/renderer/hooks/useWindowRevealHold'

/**
 * Wie lange die Aufgaben-Details auf- und zufahren. Dieselbe Viertelsekunde wie
 * der geteilte Canvas — und dieselbe Zahl steht ein zweites Mal in
 * `ProjectPage.scss` (`project-page-list-shrink` / `-grow`). Wer hier dreht,
 * dreht dort mit.
 */

/** Ein archivierter Kopf im Geschafft-Schalter klappt nicht ein (#193). */
const noopSection = () => {}

interface ProjectPageProps {
  lifeSpace?: 'personal' | 'work' | 'home'
  /**
   * When rendered through the TabStack, `projectId` is passed directly from
   * the tab's path. Falls back to `useParams` for Route-based callers.
   */
  projectId?: string
  /** When rendered through TabStack, the owning tab is passed directly.
   *  Avoids subscribing to `activeTab` via context — that subscription used
   *  to re-render every ProjectPage on every tab switch. */
  tab?: Tab
}

export const ProjectPage: React.FC<ProjectPageProps> = ({ projectId: projectIdProp, lifeSpace: lifeSpaceProp, tab }) => {
  const isActive = useIsActiveTab()
  const { isListMode, isStackMode, isCompactMode, listSectionId } = useWindowMode()
  const { projectId: projectIdParam } = useParams<{ projectId: string }>()
  const projectId = projectIdProp ?? projectIdParam
  const [searchParams, setSearchParams] = useSearchParams()
  const { t } = useTranslation()
  const dataSource = useDataSource()
  const noteIntro = useIntroNote()
  const projectRow = dataSource.useProject(projectId)
  const project = projectRow ?? null
  const loading = projectRow === undefined
  const updateProject = useCallback(
    async (changes: Partial<UpdateProject>) => { if (projectId) await dataSource.updateProject(projectId, changes) },
    [dataSource, projectId],
  )
  // Neu favorisiert landet unten in „Favoriten“: die Zeit in ms ist größer als jede frühere Stelle (#210).
  const setProjectFavorite = useCallback(
    (favorite: boolean) => { void updateProject({ favoriteOrder: favorite ? Date.now() : null }) },
    [updateProject],
  )
  // Prefer the explicit tab-prop lifespace; fall back to the project's own
  // lifeSpace once loaded; otherwise 'personal' until the load resolves so
  // useTaskCreation doesn't receive `undefined`.
  const lifeSpace: 'personal' | 'work' | 'home' = lifeSpaceProp ?? project?.lifeSpace ?? 'personal'
  // `tab` prop is passed by TabContent (TabStack). We intentionally do NOT
  // subscribe to `activeTab` via context — that would re-render every
  // ProjectPage (not just the active one) on every tab switch.
  const ownTab = tab
  const { updateTab, closeActiveTab } = useTabActions()
  const { setTitle: setTitleOfBreadcrumb } = useBreadcrumbTitle()
  // Der Titel gehört diesem Projekt — die Breadcrumb zeigt ihn nur, solange es offen ist (#185).
  const setBreadcrumbTitle = useCallback(
    (title: string | null) => setTitleOfBreadcrumb(title, projectId ?? null),
    [setTitleOfBreadcrumb, projectId],
  )
  // Bei offenem Canvas ist die Breadcrumb absolut positioniert (MainLayout)
  // und belegt keine Zeile mehr — der Kopf gleicht das aus.
  const { isCanvasFocused, canvasMode } = useCanvasMode(useTabId())
  const [hoveredSegmentIndex, setHoveredSegmentIndex] = useState<number | null>(null)
  const [lastInteractedSegmentIndex, setLastInteractedSegmentIndex] = useState(0)

  const [optimisticSectionUpdates, setOptimisticSectionUpdates] = useState<OptimisticSectionUpdates>(null)

  // Stable ref object per segment key (for TaskList handleRef prop)
  const getSegmentHandleRef = useCallback((key: string) => {
    const map = segmentHandleRefs.current
    if (!map.has(key)) map.set(key, { current: null })
    return map.get(key)!
  }, [])

  // Batch textarea auto-resize into a single rAF to avoid per-keystroke layout thrashing.
  // Reading scrollHeight right after setting height='auto' is a forced reflow — doing it
  // inside rAF lets React paint the new text first, so the measurement is done once per frame.
  const scheduleAutoResize = useCallback((el: HTMLTextAreaElement) => {
    requestAnimationFrame(() => {
      el.style.height = 'auto'
      el.style.height = `${el.scrollHeight}px`
    })
  }, [])

  const pageRef = useRef<HTMLDivElement>(null)
  // Wer ⌘Z bekommt: die zuletzt berührte Hälfte (ADR 0028).
  useUndoRegionTracking(pageRef)
  // Die scrollende Listenspalte. Trägt die Verläufe — die von `TabStack` feuern
  // hier nicht mehr, seit der Slot nicht mehr scrollt (ADR 0006).
  const bodyMainRef = useRef<HTMLDivElement>(null)
  const listColumnRef = useRef<HTMLDivElement>(null)
  // Der Körper: Bezugsrahmen des Slide-overs und die Breite, an der sich
  // entscheidet, ob die **Aufgaben-Details** überhaupt als Spalte danebenpassen.
  const bodyRef = useRef<HTMLDivElement>(null)

  // Die **Bildlaufleiste** (CONTEXT.md): unsichtbar in Ruhe, sichtbar beim
  // Scrollen, wachgehalten von der Mausbewegung. Der Ref geht hinein statt
  // heraus — er hängt hier ohnehin schon für die Verlaufshöhen.
  //
  // Hier stand ein reines `:hover` im SCSS. Das zeigte die Leiste, solange der
  // Zeiger über der Liste ruhte, und hätte das Ausblenden bei Stillstand
  // dauerhaft überdeckt.
  const { visible: scrollbarVisible, onScroll: handleListScroll } =
    useScrollbarVisibility({ ref: bodyMainRef })

  // Die Verläufe oben und unten — derselbe Baustein wie Geschafft und Kurz notiert.
  useScrollFades(bodyMainRef, listColumnRef)

  const taskListRef = useRef<HTMLDivElement>(null)
  const taskListHandleRef = useRef<TaskListHandle | null>(null)
  const segmentHandleRefs = useRef(new Map<string, { current: TaskListHandle | null }>())
  // Ref bridges: values defined after useProjectNavigation that earlier callbacks need.
  const navFocusFirstRef = useRef<(() => void) | null>(null)
  const navClearSelectionRef = useRef<(() => void) | null>(null)
  const navSelectTaskRef = useRef<((taskId: string) => boolean) | null>(null)
  // Eine eingehängte Aufgabe zeigen (#204, Grill 7) — braucht Abschnitte und Aufklappen von weiter unten.
  const revealNestedRef = useRef<((parentId: string, subtaskId: string) => () => void) | null>(null)
  const openCollapsedSectionOfRef = useRef<(taskId: string) => void>(() => {})
  const navSelectSectionRef = useRef<((sectionId: string) => void) | null>(null)
  const navFocusedItemRef = useRef<{ type: 'task' | 'section'; id: string } | null>(null)
  const activeSegmentRef = useRef(0)
  const isDragAnimatingRef = useRef(false)

  // ── Tab switch: data queries + save/restore selection ──
  // Seed from the module-level cache so the selection survives ProjectPage
  // unmount/remount (e.g. switching to a Quick Notes tab and back).
  const selectionCacheRef = useRef(projectSelectionCache)
  const {
    tasks, sections, pendingRestoreRef, pendingSelectionRestoreRef,
    correctDataReady,
  } = usePageCrossfade({ projectId, project })

  // Optimistic reorder: applied synchronously on drop so dnd-kit's transition
  // can animate. Each entry is dropped once the raw Dexie data confirms it.
  // Lives here (not in useTaskDragAndDrop) because segments useMemo reads it,
  // and the hook reads segments — keeping the state here breaks the circular dependency.
  const [optimisticUpdates, setOptimisticUpdates] = useOptimisticOrder(tasks)

  // ── Optimistic per-task / per-section field overrides ──
  // Let mutation handlers (toggle complete, badge updates, section
  // collapse / rename / delete, task delete / duplicate) show their effect
  // in the next paint instead of waiting on the IDB write + LiveQuery
  // refire (~1–2s when sync push/pull cycles are competing for the main
  // thread). Each hook auto-clears entries once the raw Dexie data
  // catches up — comparison runs against the RAW arrays, NOT against
  // override-applied data, otherwise cleanup would fire on the first
  // render and snap the UI back to the old value.
  const { optimisticTaskOverrides, setOptimisticTaskOverride } =
    useOptimisticTaskOverrides(tasks)
  const { optimisticSectionOverrides, setOptimisticSectionOverride } =
    useOptimisticSectionOverrides(sections)

  // ── Effective (override-applied) snapshots ──
  // Live OUTSIDE the segments memo so mutation handlers (toggle, collapse,
  // …) read the same view the user sees. Without this, two rapid clicks
  // both look at the still-stale raw Dexie data and flip in the *same*
  // direction — second click visually does nothing.
  const effectiveTasks = useMemo<Task[] | undefined>(() => {
    if (!tasks) return undefined
    // Paused tasks (ruhend) stay in the project list — TaskEntry renders
    // them with the dormant treatment (dashed-arc check, stone tint when
    // selected, three-dots reactivate trigger). They're identifiable in
    // place instead of vanishing into the Ruhend view.
    if (!optimisticUpdates && optimisticTaskOverrides.size === 0) {
      return tasks
    }
    return tasks.flatMap(t => {
      const dragUpdate = optimisticUpdates?.get(t.id)
      const fieldUpdate = optimisticTaskOverrides.get(t.id)
      if (!dragUpdate && !fieldUpdate) {
        return [t]
      }
      const merged = {
        ...t,
        ...(dragUpdate ? {
          order: dragUpdate.order,
          ...(dragUpdate.sectionId !== undefined ? { sectionId: dragUpdate.sectionId } : {}),
        } : {}),
        ...(fieldUpdate ?? {}),
      }
      if (merged.deletedAt) return [] // optimistic delete
      return [merged]
    })
  }, [tasks, optimisticUpdates, optimisticTaskOverrides])

  const effectiveSections = useMemo<Section[] | undefined>(() => {
    if (!sections) return undefined
    if (!optimisticSectionUpdates && optimisticSectionOverrides.size === 0) return sections
    return sections.flatMap(s => {
      const orderUpdate = optimisticSectionUpdates?.get(s.id)
      const fieldUpdate = optimisticSectionOverrides.get(s.id)
      if (!orderUpdate && !fieldUpdate) return [s]
      const merged = {
        ...s,
        ...(orderUpdate ? { order: orderUpdate.order } : {}),
        ...(fieldUpdate ?? {}),
      }
      if (merged.deletedAt) return [] // optimistic delete
      return [merged]
    })
  }, [sections, optimisticSectionUpdates, optimisticSectionOverrides])

  // ── Der Geschafft-Schalter ──
  // Die **Liegezeit** hält die frisch abgehakte Zeile zwei Sekunden an ihrem
  // Platz, danach liegt sie hinter dem Schalter am Fuß der Seite. `hold` liest
  // den Zug aus dem Ref, das weiter unten gesetzt wird (eine Render-Runde
  // versetzt, und dnd-kit rendert während eines Zugs ohnehin laufend neu):
  // Eine Zeile, die mitten in einer Ziehgeste aus dem `SortableContext` fiele,
  // risse dnd-kit das Element unter der Hand weg.
  const { graceIds, markCompleted } = useCompletionGrace({ hold: isDragAnimatingRef.current })
  // Beginnt jeden Besuch zu (CONTEXT.md) und wird nirgends gemerkt.
  const [completedOpen, setCompletedOpen] = useState(false)

  // Build render segments: interleaved sections + task groups sorted by order
  const segments = useMemo<RenderSegment[]>(() => {
    if (!effectiveTasks || !effectiveSections) {
      const shown = effectiveTasks?.filter(task => !isHiddenCompleted(task, graceIds)) ?? []
      return [{ section: null, tasks: shown, taskCount: effectiveTasks?.length ?? 0 }]
    }

    // Merge tasks + sections into a single sorted list
    type Item = { type: 'task'; data: Task } | { type: 'section'; data: Section }
    const items: Item[] = [
      ...effectiveTasks.map(t => ({ type: 'task' as const, data: t })),
      ...effectiveSections.map(s => ({ type: 'section' as const, data: s })),
    ]
    items.sort((a, b) => a.data.order - b.data.order)

    // Build collapsed set from EFFECTIVE sections so optimistic collapse
    // overrides flip the section view in the next paint instead of
    // waiting on the IDB → LiveQuery roundtrip.
    // Archivierte nie: Ihr Kopf hat keinen Pfeil, eingeklappt blieben ihre offenen Aufgaben verborgen.
    const collapsedIds = new Set(effectiveSections.filter(s => s.collapsed && !s.archivedAt).map(s => s.id))

    // Walk items, build segments
    type MutableSegment = RenderSegment & { allTasks: Task[] }
    const result: MutableSegment[] = []
    let current: MutableSegment = { section: null, tasks: [], taskCount: 0, allTasks: [] }

    for (const item of items) {
      if (item.type === 'section') {
        // Flush current segment
        if (current.section || current.allTasks.length > 0) {
          result.push(current)
        }
        current = {
          section: item.data,
          tasks: [],
          taskCount: 0,
          collapsed: collapsedIds.has(item.data.id),
          allTasks: [],
        }
      } else {
        /*
          Erledigt und die Liegezeit ist um: Die Aufgabe behält ihren Platz in
          `order` und `sectionId` (ADR 0019), sie wird nur nicht gezeigt — aus
          BEIDEN Feldern, sonst verschwände sie aus der Navigation, aber nicht
          aus dem Bild.

          **Und aus der Zahl am Abschnitts-Kopf** (Abnahme F-PR1, 21.09.2026).
          Sie stand hier eine Zeile höher und zählte weiter mit — ein Abschnitt,
          unter dem nichts mehr stand, trug dann immer noch seine „16". Die Zahl
          sagt jetzt, was zu tun ist; sind alle erledigt, zeigt `SectionHeader`
          gar keine mehr. Eine Aufgabe in ihrer Liegezeit steht noch da und
          zählt darum auch noch: So gehen Zeile und Zahl gemeinsam.
        */
        if (isHiddenCompleted(item.data, graceIds)) continue
        // Eingeklappte Abschnitte zählen mit: `allTasks` hält die volle Liste für
        // das montierte Rendern, `tasks` lässt sie weg, damit Navigation, Ziehen
        // und Gating sich nicht ändern.
        current.taskCount++
        current.allTasks.push(item.data)
        if (current.section && collapsedIds.has(current.section.id)) continue
        current.tasks.push(item.data)
      }
    }
    // Flush last segment
    if (current.section || current.allTasks.length > 0) {
      result.push(current)
    }

    /*
      **Archivierte Abschnitte** (#193) liegen hinter dem Geschafft-Schalter — es sei denn, sie
      tragen Offenes (Haken dort herausgenommen, ein älteres Gerät, der Sync). Dann stehen sie hier
      mit der Marke „Archiviert“: Nichts Offenes wird versteckt. Leer heißt dasselbe wie bei der
      Zahl am Kopf: nichts, was hier zu sehen wäre.
    */
    const listed = result.filter(seg => !seg.section?.archivedAt || seg.allTasks.length > 0)

    // Ensure at least one segment so AddTask renders in empty projects
    if (listed.length === 0) {
      listed.push({ section: null, tasks: [], taskCount: 0, allTasks: [] })
    }

    return listed
  }, [effectiveTasks, effectiveSections, graceIds])

  // List mode with a focused section (section toggle): render only that section
  // so the user can concentrate on a single section. Project-scope list (or full
  // mode) shows all segments.
  const visibleSegments = useMemo<RenderSegment[]>(() => {
    if (!isListMode || !listSectionId) return segments
    const focused = segments.filter(s => s.section?.id === listSectionId)
    return focused.length > 0 ? focused : segments
  }, [segments, isListMode, listSectionId])
  /*
    Zeigt die Liste nur diesen einen Abschnitt, hat ein neuer Abschnitt kein
    sichtbares Ziel: Er stünde am Projektende, außerhalb der Ansicht, und bliebe
    ohne Namen. Dort zeigt die Listen-Werkzeug-Pille nur „+ Neue Aufgabe", und
    ⇧A/⇧B/⇧F und das Menü legen nichts an (Diff-Audit #229, Entscheidung Sven). `visibleSegments` ist genau dann eine
    eigene Liste, wenn es auf diesen Abschnitt filtert.
  */
  const showsSingleSection = visibleSegments !== segments

  // Was hinter dem Schalter liegt: die erledigten Aufgaben des Projekts,
  // zuletzt Erledigtes oben. Im **Listen-Modus** mit ausgewähltem Abschnitt
  // nur dessen — der Fuß gehört der Seite, und die Seite zeigt dort einen
  // Abschnitt.
  //
  // Darunter die **archivierten Abschnitte**, die nicht in der Liste stehen (#193), je mit ihren
  // erledigten Aufgaben. `completedTasks` kommt in der gezeichneten Reihenfolge — die Pfeiltasten
  // laufen so, wie man sieht.
  const { tasks: completedTasks, groups: archivedGroups } = useMemo(() => {
    if (!effectiveTasks) return { tasks: [] as Task[], groups: [] }
    const scoped = isListMode && listSectionId
      ? effectiveTasks.filter(task => task.sectionId === listSectionId)
      : effectiveTasks
    const completed = splitCompleted(scoped, graceIds).completed
    if (isListMode && listSectionId) return { tasks: completed, groups: [] }
    const listed = new Set(segments.flatMap(seg => (seg.section ? [seg.section.id] : [])))
    const archived = (effectiveSections ?? []).filter(section => section.archivedAt && !listed.has(section.id))
    return groupArchivedCompleted(completed, archived)
  }, [effectiveTasks, effectiveSections, segments, graceIds, isListMode, listSectionId])

  // Je archiviertem Abschnitt die Zeilen, die das Archivieren abgehakt hat — das Menü bietet damit
  // „Mit 3 Aufgaben wiederherstellen“ an (#193).
  const archiveReopenCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const section of effectiveSections ?? []) {
      if (!section.archivedAt) continue
      const count = (effectiveTasks ?? []).filter(task => isClosedByArchive(task, section)).length
      if (count > 0) counts.set(section.id, count)
    }
    return counts
  }, [effectiveSections, effectiveTasks])

  // Stack mode: flatten the segments (list order, collapsed sections excluded)
  // into the deck queue. Completed tasks stay included — TaskStackView filters
  // them itself so a just-checked focus card can linger before the deck
  // advances (the optimistic status flip would otherwise remove it instantly).
  const stackQueue = useMemo<Task[]>(
    () => (isStackMode ? segments.flatMap(s => s.tasks) : []),
    [segments, isStackMode],
  )

  // Keep taskListHandleRef in sync with the first segment's handle (for focusFirst, selectTask, etc.)
  useEffect(() => {
    if (segments.length === 0) return
    const firstKey = segments[0].section?.id
    if (firstKey) {
      const segRef = segmentHandleRefs.current.get(firstKey)
      if (segRef) taskListHandleRef.current = segRef.current
    }
  }, [segments])

  // ── Scroll to section (triggered by sidebar sub-menu click or initial load) ──
  // Uses a dedicated event instead of watching searchParams to avoid a feedback
  // loop with scroll-spy: scroll-spy updates ?section= → this effect fires →
  // scrollIntoView fights the user's ongoing scroll momentum.
  const lastScrolledSectionRef = useRef<string | null>(searchParams.get('section'))
  useEffect(() => {
    let outerTimer: ReturnType<typeof setTimeout> | null = null
    let innerTimer: ReturnType<typeof setTimeout> | null = null
    const cleanup = onAppEvent('scroll-to-section', (sectionId) => {
      if (lastScrolledSectionRef.current === sectionId) return
      lastScrolledSectionRef.current = sectionId
      isScrollSpySuppressedRef.current = true
      if (outerTimer) clearTimeout(outerTimer)
      outerTimer = setTimeout(() => {
        const el = document.querySelector<HTMLElement>(`.section-header[data-section-id="${sectionId}"]`)
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
        if (innerTimer) clearTimeout(innerTimer)
        innerTimer = setTimeout(() => {
          isScrollSpySuppressedRef.current = false
        }, 600)
      }, 100)
    })
    return () => {
      cleanup()
      if (outerTimer) clearTimeout(outerTimer)
      if (innerTimer) clearTimeout(innerTimer)
    }
  }, [])

  // On initial load with ?section= param, scroll to that section once data is ready
  useEffect(() => {
    if (!correctDataReady) return
    const sectionId = searchParams.get('section')
    if (!sectionId) return
    if (lastScrolledSectionRef.current === sectionId) return
    dispatchAppEvent('scroll-to-section', sectionId)
  // Only on initial data ready, not on searchParams changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [correctDataReady])

  // After the "Im Projekt aufgeweckt"-toast button opens this project,
  // a one-shot stash carries the woken task's id. Consume it whenever
  // this project tab becomes active with data ready: when the tab was
  // pre-mounted via TabStack `display: none`, neither `correctDataReady`
  // nor `projectId` change on activation — only `isActive` does, so it
  // has to be in the deps for the consume to fire on tab-switch. The
  // stash itself is one-shot, so re-firing without a pending id is a
  // cheap no-op.
  /**
   * Eine Aufgabe zeigen: hinscrollen und auswählen.
   *
   * Zwei Wege benutzen das — der Aufgeweckt-Hinweis unten und, seit dem
   * **Übernehmen**, das **Eselsohr** eines Canvas-Elements samt dem Hinweis
   * danach. Deshalb herausgezogen statt ein zweites Mal gebaut.
   *
   * Es wartet, bis die Seite fertig gezeichnet ist: Vorher hat die Zeile
   * ihre endgültige Lage nicht.
   *
   * Gibt einen Aufräumer zurück, damit ein Effekt das Warten abbestellen
   * kann; wer direkt ruft, ignoriert ihn.
   */
  const revealTask = useCallback((taskId: string, onSelected?: () => void) => {
    /*
      Die Zeile SUCHEN statt sie vorauszusetzen — und erst auswählen, wenn die Seite fertig ist.

      Beim Aufgeweckt-Hinweis stand sie nach kurzer Zeit immer schon da. Beim **Übernehmen** ist
      sie brandneu, und beim **Wieder öffnen** von **Geschafft** steht sie in Dexie noch als
      erledigt: Dexie schreibt, die Live-Query meldet, React zeichnet — das kann länger dauern. Ein
      einmaliger Griff ins Leere ließe die Liste stehen, wo sie war, und die Aufgabe unausgewählt
      (gemeldet 2026-09-12).

      Ausgewählt und gerollt wird, wenn die Zeile dasteht UND die Seite still steht
      (`whenTaskRowSettled`): Mitten im Laden gerollt, rutschte sie wieder aus dem Bild (gemeldet
      2026-10-01, Sprung aus „Favoriten“). Die sichtbare Auswahl kommt über `navSelectTaskRef`
      (dieselbe wie bei Cmd/Klick); sie sagt selbst, ob sie gegriffen hat.
    */
    return whenTaskRowSettled({
      root: () => pageRef.current,
      taskId,
      run: row => {
        if (navSelectTaskRef.current?.(taskId)) onSelected?.()
        row.scrollIntoView({ behavior: 'smooth', block: 'center' })
      },
    })
  }, [])


  // ── Scroll-spy: update ?section= param as user scrolls past section headers ──
  const isScrollSpySuppressedRef = useRef(false)
  const scrollSpySectionRef = useRef<string | null>(searchParams.get('section'))

  // Listen for suppress events from sidebar (e.g. project click → scroll to top)
  useEffect(() => {
    let suppressReleaseTimer: ReturnType<typeof setTimeout> | null = null
    const cleanupSuppress = onAppEvent('scroll-spy-suppress', () => {
      isScrollSpySuppressedRef.current = true
      scrollSpySectionRef.current = null
      lastScrolledSectionRef.current = null
      if (suppressReleaseTimer) clearTimeout(suppressReleaseTimer)
      suppressReleaseTimer = setTimeout(() => {
        isScrollSpySuppressedRef.current = false
      }, 800)
    })
    // Keyboard navigation (CMD/Alt+Arrow) knows which section it landed in.
    // Update the sidebar directly so the active sub-item reflects the target.
    const cleanupSetSection = onAppEvent('scroll-spy-set-section', (sectionId) => {
      const current = scrollSpySectionRef.current
      if (sectionId && sectionId !== current) {
        scrollSpySectionRef.current = sectionId
        lastScrolledSectionRef.current = sectionId
        setSearchParams({ section: sectionId }, { replace: true })
      } else if (!sectionId && current) {
        scrollSpySectionRef.current = null
        lastScrolledSectionRef.current = null
        setSearchParams({}, { replace: true })
      }
    })
    return () => {
      cleanupSuppress()
      cleanupSetSection()
      if (suppressReleaseTimer) clearTimeout(suppressReleaseTimer)
    }
  }, [setSearchParams])

  // Keep ref in sync with external param changes (e.g. sidebar click)
  useEffect(() => {
    scrollSpySectionRef.current = searchParams.get('section')
  }, [searchParams])

  useEffect(() => {
    if (!correctDataReady || !sections || sections.length === 0) return

    const scrollContainer = getScrollContainer(pageRef.current)
    if (!scrollContainer) return

    // Throttle: skip if a rAF is already pending
    let rafId: number | null = null

    const handleScroll = () => {
      if (isScrollSpySuppressedRef.current) return
      if (rafId !== null) return
      rafId = requestAnimationFrame(() => {
        rafId = null
        computeActiveSection(scrollContainer)
      })
    }

    const computeActiveSection = (container: HTMLElement) => {
      const headerEls = container.querySelectorAll<HTMLElement>('.section-header[data-section-id]')
      if (headerEls.length === 0) return

      const containerRect = container.getBoundingClientRect()
      // Activation line: 1/3 from the top of the scroll container.
      // A section becomes active when its header scrolls above this line.
      // This ensures the last section can become active even when the user
      // can't scroll further (content ends before the header reaches the top).
      const activationY = containerRect.top + containerRect.height / 3

      let activeId: string | null = null
      for (const el of headerEls) {
        const rect = el.getBoundingClientRect()
        // Header top is above the activation line → this section is active
        if (rect.top < activationY) {
          activeId = el.dataset.sectionId ?? null
        }
      }

      const currentSection = scrollSpySectionRef.current
      if (activeId && activeId !== currentSection) {
        scrollSpySectionRef.current = activeId
        lastScrolledSectionRef.current = activeId
        setSearchParams({ section: activeId }, { replace: true })
      } else if (!activeId && currentSection) {
        scrollSpySectionRef.current = null
        lastScrolledSectionRef.current = null
        setSearchParams({}, { replace: true })
      }
    }

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll)
      if (rafId !== null) cancelAnimationFrame(rafId)
    }
  }, [correctDataReady, sections, setSearchParams])



  // ── Project header (title, notes, breadcrumb, tab sync) ──
  const {
    name, setName, nameHtml, setNameHtml, savedName,
    notesValue, setNotesValue, savedNotes,
    isEditingHeader, setIsEditingHeader,
    titleRef, notesRef, headerRef,
    handleNotesKeyDown, handleHeaderBlur,
    handleTitleSubmit, handleTitleCommit, handleIconChange, handleTitleArrowDown, handleRevert,
  } = useProjectHeader({
    project, updateProject, projectId,
    activeTab: ownTab, updateTab, closeActiveTab, setBreadcrumbTitle,
    navFocusFirstRef, t,
    isActive,
  })

  // Die Knöpfe neben dem Titel weichen erst, wenn der Titel nicht mehr daneben passt — zuerst ihre Beschriftungen.
  const { rowRef: headerRowRef, actionsRef: headerActionsRef, iconRowRef: headerIconRowRef, actionsMode: headerActionsMode } =
    useHeaderActionsFit(titleRef, name || t('project.defaultName'))
  const headerActionsHidden = headerActionsMode === 'hidden'

  // ── Task creation ──
  const {
    creatingPosition, creatingAfterTaskId, creatingSegmentIdx, pendingInlineTarget, morphData, positionMorphKey, inlineMorphTitle, inlineMorphSelected, inlineMorphKey, creationKey, landed: creationLanded, inlineResetKey, skipInlineEntrance, autoSelectId,
    startCreating, startCreatingAfter,
    handleCreateTask, handleCreateTaskAndContinue,
    handleCreateTaskAfter, handleCreateTaskAfterAndContinue,
    handleCancelCreate, handleMorphComplete, handleInlineMorphComplete,
    topCreateRef, bottomCreateRef,
  } = useTaskCreation({
    projectId, lifeSpace, segments, drawnSegments: visibleSegments, tasks, sections,
    activeSegmentRef, navClearSelectionRef, navSelectTaskRef, navSelectSectionRef, navFocusedItemRef,
    setLastInteractedSegmentIndex,
  })

  // ── Projekt-Canvas empty-state (Phase 1) ──
  // A brand-new project (no tasks, no Projekt-Canvas notes) shows a teaser
  // instead of the task board/canvas entry toolbar. Canvas note creation
  // doesn't exist yet (Phase 2+), so this count is currently always 0 — the
  // check is still correct now so nothing has to change once notes exist.
  const canvasNotes = useCanvasNotes(projectId)
  const isCreatingTask = creatingPosition !== null || creatingAfterTaskId !== null
  // Der Zustand des Projekt-Canvas liegt hier statt im Panel: bei leerem
  // Projekt wird das Panel gar nicht gerendert (dort steht der Teaser), das
  // Tastenkürzel muss aber auch dann öffnen können.
  const [canvasPanelState, setCanvasPanelState] = useState<CanvasPanelState>('closed')
  const canvasIsOpen = canvasPanelState !== 'closed'
  /*
    Notiz/Memo der **Listen-Werkzeug-Pille** stellen das Werkzeug der
    Pinnwand scharf. Ohne Aufgaben gibt es keine Liste, neben der sie stehen
    könnte — dann geht sie maximiert auf.
  */
  const [canvasPlacementRequest, setCanvasPlacementRequest] = useState<CanvasPlacementRequest | null>(null)
  /*
    Nur ein Zettel aus der LISTEN-Pille lässt sie abtauchen — sie selbst wurde bedient. Kommt er aus
    der Werkzeug-Pille der Pinnwand, hat die Listen-Pille damit nichts zu tun und bleibt stehen
    (gemeldet 02.10.2026, geteilte Pinnwand). Gemerkt bis zum Ende des Setzens.
  */
  const [placingFromList, setPlacingFromList] = useState(false)
  const requestCanvasPlacement = useCallback((kind: 'note' | 'memo', drag = false) => {
    setPlacingFromList(true)
    setCanvasPlacementRequest(prev => ({ kind, drag, seq: (prev?.seq ?? 0) + 1 }))
  }, [])
  const canvasOpenTarget: 'maximized' | 'split' = effectiveTasks?.length === 0 ? 'maximized' : 'split'

  /*
    Die **Listen-Werkzeug-Pille** taucht ab, solange angelegt wird (#229): ein
    Ersteller offen, ein frischer Abschnitt ohne Namen, ein Zettel am Zeiger.
    Die beiden letzten meldet erst der Abschnittskopf bzw. das Panel.
  */
  const [namingSectionId, setNamingSectionId] = useState<string | null>(null)
  const handleSectionNamingChange = useCallback((sectionId: string, naming: boolean) => {
    setNamingSectionId(prev => (naming ? sectionId : prev === sectionId ? null : prev))
  }, [])
  const [canvasPlacing, setCanvasPlacing] = useState(false)
  const handleCanvasPlacingChange = useCallback((placing: boolean) => {
    setCanvasPlacing(placing)
    if (!placing) setPlacingFromList(false)
  }, [])

  /**
   * **Die Aufgaben-Details** — offen oder zu wie der Canvas, und in welcher
   * Gestalt sie offen stehen. EIN Zustand für beides, und das ist der Kern:
   *
   * **Eine einzige Gestalt** (ADR 0023), also ein Bool statt eines Werts mit
   * drei Möglichkeiten. Das Slide-over ist ersatzlos gestrichen: Seine beiden
   * Gründe sind weg — der Canvas schließt die Aufgaben-Details inzwischen beim
   * Öffnen, und ein zu schmales Fenster quetscht jetzt ausdrücklich die Liste.
   * Mit ihm fällt die ganze Regel „keine Gestaltwandlung im Betrieb": Bei
   * einer Gestalt gibt es nichts einzufrieren.
   *
   * Der Preis bleibt derselbe wie zuvor für die feste Spalte: Wer das Fenster
   * mit offener Karte schmal zieht, quetscht die Liste.
   *
   * Kein Gedächtnis am Projekt: Der Zustand lebt in der Sitzung.
   */
  /**
   * Der Griff selbst entsteht erst nach der Navigation (er braucht ihre
   * Auswahl); was hier oben schon danach fragt, geht über diesen Ref.
   */
  const detailsRef = useRef<UseTaskDetailsPane | null>(null)

  /*
    Keine Aufgaben-Details im Stapel- und im Listen-Modus (CONTEXT.md): Der Kopf
    mit dem Knopf ist dort ohnehin weg, und ein Kartenstapel hat keine Spalte
    daneben. Der gemerkte Zustand bleibt dabei stehen — wer zurückschaltet,
    findet ihn wieder.
  */
  const detailsAllowed = !isStackMode && !isCompactMode


  /**
   * Für welches Projekt der gemerkte Canvas-Zustand schon geholt wurde.
   *
   * **State und kein Ref, und geprüft WÄHREND des Renderns** — das ist Absicht
   * und war der zweite Anlauf. Der erste holte den Zustand in einem
   * `useLayoutEffect`; der läuft nach dem Commit, das Panel stand also einen
   * Commit lang auf `--closed` im DOM. Kein Bild dazwischen — aber das
   * **Drag Handle** misst in SEINEM Layout-Effekt (`getBoundingClientRect`),
   * und Layout-Effekte laufen von unten nach oben: Diese Messung erzwingt eine
   * Stilberechnung, die den geschlossenen Zustand festhält. Der Wechsel danach
   * hatte damit einen gültigen Startwert — und der 250ms-Übergang lief. Genau
   * das war zu sehen: erst die volle Aufgabenliste, dann fuhr der Canvas auf.
   *
   * In der Renderphase gesetzt, verwirft React den angefangenen Durchlauf und
   * rendert sofort neu, BEVOR irgendetwas ins DOM geht. Das Panel wird direkt
   * als `--maximized` eingesetzt — ein frisch eingefügtes Element hat keinen
   * Vorher-Wert, es gibt also nichts zu animieren.
   *
   * State und nicht Ref, weil StrictMode die Renderfunktion doppelt aufruft:
   * Ein im verworfenen Durchlauf gesetzter Ref ließe den zweiten die Bedingung
   * überspringen — der Zustand käme nie an. State wird nicht an Ort und Stelle
   * verändert und übersteht das. Es ist das Muster „State anpassen, wenn sich
   * eine Prop ändert" aus Reacts eigener Anleitung.
   *
   * Der Riegel selbst bleibt der Kern des Ganzen: Ohne ihn zöge jede spätere
   * Meldung der Live-Query den Zustand zurück — man öffnet den Canvas, ein
   * Sync-Zyklus meldet das Projekt neu, und er klappt wieder zu. Über die
   * Projekt-Kennung und nicht über ein blankes `didRestore`, weil dieser Baum
   * beim Projektwechsel stehen bleibt (`TabStack` keyt nach `tab.id`): Ein
   * zweites Projekt soll seinen eigenen Zustand bekommen.
   */
  const [restoredCanvasStateFor, setRestoredCanvasStateFor] = useState<string | null>(null)

  /**
   * Der erste Anblick nach dem Wiederherstellen ist eine AUSGANGSLAGE, keine
   * Bewegung — für genau ein Bild werden die Übergänge stillgelegt.
   *
   * Es reicht NICHT, den Zustand rechtzeitig zu setzen. Was den geöffneten
   * Canvas ausmacht, hängt nicht am Panel-Zustand, sondern am
   * `CanvasFocusContext` — und den meldet das Panel erst in seinem eigenen
   * Layout-Effekt, einen Commit später. Daran hängen drei Bewegungen, und die
   * auffälligste liegt weit außerhalb dieser Seite:
   *
   *   1. die Aufgabenliste, die nach links ausfährt (`--canvas-maximized`)
   *   2. **die Navigationsleisten, die auf 0 zusammenfahren** — 240px, dabei
   *      rutscht die linke Kante des Canvas sichtbar mit nach links
   *   3. die Inhaltskarte (`margin-left`) und die Breadcrumb-Zeile
   *
   * Deshalb hängt die Stilllegung am `html` und nicht an dieser Seite: Von
   * `.project-page` aus sind 2 und 3 nicht erreichbar. Die Regel steht in
   * `globals.scss` neben `canvas-resizing`, dem Hausmuster für genau das.
   *
   * Nur wenn tatsächlich etwas zu bewegen wäre: Bei `'closed'` steht alles
   * schon dort, wo es hingehört.
   */
  const [canvasRestorePaint, setCanvasRestorePaint] = useState(false)

  if (project && project.id === projectId && restoredCanvasStateFor !== projectId) {
    // Veraltete Zeile: `useLiveQuery` liefert beim Projektwechsel weiter die
    // Daten des vorigen Projekts, bis die neue Abfrage aufgelöst ist (siehe
    // `usePageCrossfade`). Die Bedingung oben hält sie draußen.
    const restored = project.canvasPanelState ?? 'closed'
    setRestoredCanvasStateFor(projectId)
    setCanvasPanelState(restored)
    // Auch geschlossen kann sich etwas bewegen: Liegen Elemente auf dem
    // Canvas, steht der **Anriss**, und die Breadcrumb verlässt für ihn den
    // Fluss (MainLayout.scss). Sind die Elemente hier noch nicht geladen,
    // kommt der Anriss später — dann hält der Logo-Schirm (siehe unten).
    setCanvasRestorePaint(restored !== 'closed' || shouldShowCanvasPeek('closed', canvasNotes))
  }

  /**
   * Die Stilllegung anhängen — im LAYOUT-Effekt, und das ist der Punkt.
   *
   * Sie muss stehen, bevor die Meldung des Panels an den `CanvasFocusContext`
   * verarbeitet wird. Die Reihenfolge trägt: Layout-Effekte laufen von unten
   * nach oben, das Panel meldet also in seinem, dieser hier läuft danach — und
   * React spült die daraus entstehende Neuzeichnung von `MainLayout` erst nach
   * dem GESAMTEN Layout-Effekt-Durchlauf nach. Die Klasse ist zu diesem
   * Zeitpunkt da; die Leisten fahren ohne Übergang auf 0.
   *
   * Ein passiver Effekt käme zu spät, er läuft erst nach dem Zeichnen.
   */
  useLayoutEffect(() => {
    // `isActive`, weil die Klasse am DOKUMENT hängt und nicht am Tab: Beim
    // Start stellen ALLE wiederhergestellten Tabs ihren Canvas her, auch die
    // unsichtbaren hinter `display: none` (CLAUDE.md, TabStack). Ohne das Gate
    // legte ein Hintergrund-Tab die Übergänge des sichtbaren still.
    if (!canvasRestorePaint || !isActive) return
    document.documentElement.classList.add('canvas-restoring')
    return () => document.documentElement.classList.remove('canvas-restoring')
  }, [canvasRestorePaint, isActive])

  /**
   * Und wieder abnehmen, sobald das Bild steht.
   *
   * Passiver Effekt UND ein Bild extra: Der Effekt läuft nach dem Zeichnen, das
   * `requestAnimationFrame` gibt sicherheitshalber noch eines dazu. Das
   * Abnehmen selbst kann nichts auslösen — es ändert keinen einzigen Wert,
   * alles steht bereits an seinem Platz.
   */
  useEffect(() => {
    if (!canvasRestorePaint) return
    const frame = requestAnimationFrame(() => setCanvasRestorePaint(false))
    return () => cancelAnimationFrame(frame)
  }, [canvasRestorePaint])

  /**
   * Der EINE Ausgang, durch den eine Größenänderung ans Projekt geht.
   *
   * Der Vergleich steht VOR dem `set` und nicht in der Updater-Funktion: Die
   * App läuft in `React.StrictMode` (`main.tsx`), und React ruft Updater dort
   * doppelt auf — genau um Nebeneffekte wie dieses `updateProject` aufzudecken.
   * Zwei Dexie-Schreibvorgänge und zwei Queue-Zeilen je Öffnen wären die Folge;
   * die Sync-Queue dedupliziert nichts. Siehe `NoteNode` für dieselbe
   * Falle an anderer Stelle.
   *
   * **Nicht benutzt beim Wiederherstellen** — der Effekt oben setzt direkt.
   * Sonst schriebe jeder Start denselben Wert zurück und erzeugte einen
   * Sync-Eintrag für nichts.
   *
   * Der **Drag Handle** schreibt hierüber MIT, obwohl `useCanvasSplitDrag`
   * seinen `onChange` als „nur Ansicht" führt. Bewusst: Die Hysterese in
   * `resolveSplitDrag` verlangt für jeden Wechsel einen Schwenk über ein 160px
   * breites Band, der Wächter macht daraus „je bewusstem Richtungswechsel"
   * statt „je Pixel". Realistisch sind null bis zwei Schreibvorgänge je Geste —
   * nicht mehr als zweimal auf den Maximieren-Knopf zu klicken. Die Alternative
   * (eine zweite Prop am Panel, Ansicht und Persistenz getrennt) müsste an
   * sechs Aufrufstellen mitgeführt werden, und eine vergessene hieße:
   * stillschweigend nicht mehr gespeichert.
   */
  const changeCanvasPanelState = useCallback((next: CanvasPanelState) => {
    if (next === canvasPanelState) return
    setCanvasPanelState(next)
    void updateProject({ canvasPanelState: next })
    // „Entdecken › Pinnwand“ (#323): einmal aufgezogen reicht.
    if (next !== 'closed') noteIntro('board')
  }, [canvasPanelState, updateProject, noteIntro])

  const completedIdsRef = useRef<Set<string>>(new Set())
  completedIdsRef.current = new Set(completedTasks.map(task => task.id))
  const canvasPanelStateRef = useRef(canvasPanelState)
  canvasPanelStateRef.current = canvasPanelState
  const changeCanvasPanelStateRef = useRef(changeCanvasPanelState)
  changeCanvasPanelStateRef.current = changeCanvasPanelState

  /**
   * **Eine Aufgabe in der Liste zeigen — und dafür sorgen, dass es eine Liste gibt.**
   *
   * Ist der **Canvas** maximiert, ist die Aufgabenliste nach links ausgefahren und steht
   * gar nicht im DOM; `revealTask` suchte dann eine Sekunde lang vergeblich, und man
   * landete im Projekt, ohne etwas zu sehen. Also wird erst auf **geteilt** zurückgefahren
   * — dieselbe Haltung wie beim **Randpfeil** und der **Tab-Reihenfolge**: Ist das Ziel
   * nicht zu sehen, wird es sichtbar gemacht, statt die Bewegung abzulehnen.
   *
   * Drei Wege gehen hier durch: das **Eselsohr** auf dem Canvas und die beiden Zeilen des
   * Menüs auf **Geschafft** („Wieder öffnen", „Im Projekt zeigen"), die über
   * `pendingTaskJump` hereinkommen.
   *
   * **Über Refs, damit die Funktion STEHENBLEIBT** — und das ist kein Geschmack: Der
   * Effekt unten löst den gemerkten Sprung ein und gibt `revealTask`s Abbruch als
   * Aufräumer zurück. Hinge er an einer Funktion, die sich mit `canvasPanelState`
   * ändert, räumte React beim Wechsel auf geteilt genau den Sprung weg, den dieser
   * Wechsel erst möglich machen sollte: Der Canvas führe auf, die Aufgabe bliebe
   * unausgewählt, und der zweite Durchlauf fände nichts mehr in der Ablage (gemeldet
   * 2026-09-12).
   */
  const revealTaskInList = useCallback((taskId: string, options: { reopened?: boolean; onSelected?: () => void } = {}) => {
    if (canvasPanelStateRef.current === 'maximized') changeCanvasPanelStateRef.current('split')
    /*
      **Was erledigt ist, liegt hinter dem Geschafft-Schalter**, und zugeklappt steht es
      weder im Baum noch in der Navigation — `revealTask` fände weder eine Zeile zum
      Rollen noch etwas zum Auswählen. „Im Projekt zeigen" auf **Geschafft** meint aber
      immer eine erledigte Aufgabe. Also aufklappen; den Rest erledigt die gewöhnliche
      Auswahl, sobald die Zeile dasteht (gemeldet 2026-09-12).

      Eine gerade wieder geöffnete Aufgabe steht in Dexie oft noch als erledigt. Sie
      gehört in die Liste, nicht hinter den Schalter — `revealTask` wartet auf sie.
    */
    if (!options.reopened && completedIdsRef.current.has(taskId)) setCompletedOpen(true)
    // Ein zugeklappter Abschnitt verbirgt sie ebenso — aufklappen (#210, Sprung aus „Favoriten“).
    openCollapsedSectionOfRef.current(taskId)
    const plain = revealTask(taskId, options.onSelected)

    /*
      **Eine eingehängte Aufgabe steht nicht in der Liste** (#204, Grill 7): Der Zettel ihres
      Eselsohrs zeigt dann die Elternaufgabe, aufgeklappt, und darin sie selbst, angefasst. Der
      Weg oben läuft trotzdem an — für eine gewöhnliche Aufgabe ändert sich nichts; bei einer
      eingehängten griffe er ins Leere und wird abgebrochen.
    */
    let cancelled = false
    let nested: (() => void) | undefined
    // In der Liste steht nur die Aufgabe ganz oben (#181) — sie klappt auf, darin steht der ganze Baum.
    void dataSource.getTaskAncestors(taskId).then(chain => {
      const root = chain[chain.length - 1]
      if (cancelled || !root) return
      plain()
      nested = revealNestedRef.current?.(root.id, taskId)
    })
    return () => {
      cancelled = true
      plain()
      nested?.()
    }
  }, [revealTask, dataSource])

  /*
    Der gemerkte Sprung von **Geschafft** (und aus dem Aufwecken-Hinweis).

    **Er wartet auf die Aufgaben, nicht nur auf `correctDataReady`**: Das sagt bloß
    „keine Daten eines FREMDEN Projekts", und `undefined` erfüllt das auch — beim
    Aufbau eines frischen Tabs lief der Sprung deshalb, bevor irgendeine Aufgabe da
    war. Dann wusste niemand, dass das Ziel hinter dem **Geschafft-Schalter** liegt,
    und der blieb zu (gemeldet 2026-09-12).

    Gewartet wird auf ein BOOL, nicht auf die Liste selbst: `effectiveTasks` ist bei
    jeder Änderung ein neues Feld, der Effekt liefe wieder an — und sein Aufräumer
    bräche den Sprung ab, den er gerade begonnen hat.
  */
  const tasksLoaded = effectiveTasks !== undefined
  // Steht die Seite schon offen, läuft der Effekt unten sonst nicht erneut (#210: Sprung aus „Favoriten“).
  const [jumpSignal, setJumpSignal] = useState(0)
  useEffect(() => onAppEvent('pending-task-jump', () => setJumpSignal(n => n + 1)), [])
  useEffect(() => {
    if (!correctDataReady) return
    if (!isActive) return
    if (!tasksLoaded) return
    if (!projectId) return
    const pending = consumePendingTaskJump(projectId)
    if (!pending) return

    return revealTaskInList(pending.taskId, {
      reopened: pending.reopened,
      // Erst nach dem Auswählen: Vorher zeigten die Details kurz das Projekt (#210).
      onSelected: pending.openDetails ? () => detailsRef.current?.show() : undefined,
    })
  }, [correctDataReady, projectId, isActive, tasksLoaded, revealTaskInList, jumpSignal])

  /*
    **Der Sprung auf die Pinnwand** (Treffer der Suche, #233): Ist sie zu, geht sie auf — so weit,
    wie Knopf und Taste sie aufziehen (`canvasOpenTarget`). Liegen die Aufgaben-Details über ihr,
    gehen sie weg — sonst wählte der Canvas unter ihnen unsichtbar aus. Fahrt und Auswahl macht der
    Canvas, sobald das Element dasteht (`revealRequest`).
  */
  const [canvasJumpSignal, setCanvasJumpSignal] = useState(0)
  useEffect(() => onAppEvent('pending-canvas-jump', () => setCanvasJumpSignal(n => n + 1)), [])
  const [canvasReveal, setCanvasReveal] = useState<CanvasRevealRequest | null>(null)
  /*
    Die Bitte gilt EINEM Aufgehen. Der Canvas wird beim Schließen abgebaut und vergisst, was er
    schon gezeigt hat; stünde sie noch da, wählte er das Element beim nächsten Öffnen wieder aus
    und führe hin (Diff-Audit 02.10.2026). Ebenso, wenn der Tab ein anderes Projekt zeigt.
  */
  useEffect(() => {
    if (canvasPanelState === 'closed') setCanvasReveal(null)
  }, [canvasPanelState])
  useEffect(() => { setCanvasReveal(null) }, [projectId])
  const canvasOpenTargetRef = useRef(canvasOpenTarget)
  canvasOpenTargetRef.current = canvasOpenTarget
  useEffect(() => {
    if (!correctDataReady || !isActive || !tasksLoaded || !projectId) return
    const noteId = consumePendingCanvasJump(projectId)
    if (!noteId) return
    if (canvasPanelStateRef.current === 'closed') changeCanvasPanelStateRef.current(canvasOpenTargetRef.current)
    else detailsRef.current?.closeWithoutMotion()
    setCanvasReveal(previous => ({ noteId, seq: (previous?.seq ?? 0) + 1 }))
  }, [correctDataReady, projectId, isActive, tasksLoaded, canvasJumpSignal])

  /**
   * Das zuletzt gezogene Breitenverhältnis, bis der Projektwert es einholt.
   *
   * Ohne dieses Halten spränge die Spalte nach dem Loslassen für ein bis zwei
   * Sekunden auf ihre alte Breite zurück: `useOptimisticEntityOverrides`
   * dokumentiert, dass der Weg Dexie-Write → LiveQuery → Rendern genau so
   * lange dauert, wenn ein Sync-Zyklus läuft.
   *
   * Es ist dieselbe Bauart wie dort, nur ohne Apparat: „Layer 1", also
   * Wertegleichheit gegen die Rohdaten. Der dort nötige 10s-Notausstieg
   * entfällt — bei einer Zahl (oder `null`) ist der Vergleich exakt, es gibt
   * keine Gleichheit, durch die er fallen könnte.
   */
  const [pendingSplitRatio, setPendingSplitRatio] = useState<number | null | undefined>(undefined)
  const projectSplitRatio = project?.canvasSplitRatio ?? null

  useEffect(() => {
    if (pendingSplitRatio !== undefined && projectSplitRatio === pendingSplitRatio) {
      setPendingSplitRatio(undefined)
    }
  }, [pendingSplitRatio, projectSplitRatio])

  const handleSplitRatioCommit = useCallback((ratio: number | null) => {
    setPendingSplitRatio(ratio)
    void updateProject({ canvasSplitRatio: ratio })
  }, [updateProject])

  // `C` öffnet maximiert, `⇧C` im Split, beide schließen wieder — die Regel
  // steht in `nextCanvasPanelState`. (`N` gehört dem **Notizen**, siehe
  // `useCanvasPlacementShortcut`.)
  const handleToggleCanvas = useCallback((target: CanvasShortcutTarget) => {
    changeCanvasPanelState(nextCanvasPanelState(canvasPanelState, target))
  }, [canvasPanelState, changeCanvasPanelState])

  const showCanvasPanel = shouldRenderCanvasPanel({
    projectId, isCompactMode, isStackMode,
  })

  /*
    Das Kürzel hängt an `showCanvasPanel` und nicht bloß an `isActive`.

    Im **Listen-** und im **Stapel-Modus** gibt es kein Panel — das Kürzel lief
    dort aber weiter und setzte einen Zustand, den niemand sehen konnte.
    Solange nichts gespeichert wurde, war das folgenlos. Jetzt nicht mehr: Ein
    `C` im schmalen Fenster — ein blanker Buchstabe außerhalb von Eingabefeldern
    — schriebe ans Projekt, und beim nächsten Aufziehen stünde ein maximierter
    Canvas da, den man nie geöffnet hat.

    Der gemerkte Zustand bleibt in diesen Modi unangetastet am Projekt stehen.
  */
  useCanvasPanelShortcut({
    enabled: isActive && showCanvasPanel,
    onToggle: handleToggleCanvas,
  })

  /**
   * Den Logo-Schirm beim Start stehen lassen, bis dieser Canvas dasteht.
   *
   * Die Stilllegung weiter oben nimmt der Wiederherstellung die Bewegung; sie
   * kann aber nicht verhindern, dass man den Vorzustand ÜBERHAUPT sieht.
   * Zwischen „Anmeldung steht" und „Projektzeile ist aus Dexie da" liegen
   * ~7 Bilder, in denen die Aufgabenliste mit offener Seitenleiste dasteht.
   * Die füllt der Schirm.
   *
   * **Es reicht NICHT, bis zum geholten Zustand zu warten** — daran ist der
   * erste Anlauf gescheitert. Der Zustand steht in Commit N; die Seitenleiste
   * weicht aber erst in N+1, weil sie nicht am Panel-Zustand hängt, sondern am
   * `CanvasFocusContext`, den das Panel in seinem Layout-Effekt meldet. Gab der
   * Schirm schon in N frei, blendete er über eine Seite aus, auf der die
   * Leisten noch standen.
   *
   * Deshalb der zweite Teil der Bedingung: Erst wenn `canvasMode` die geholte
   * Größe TRÄGT, steht die Geometrie. Ohne gerendertes Panel (Listen- und
   * Stapel-Modus) gibt es nichts zu melden — dort gilt sie sofort.
   *
   * Nur der aktive Tab und nur mit Projektkennung: Beim Start mounten auch die
   * unsichtbaren Tabs (`TabStack` weckt sie über `requestIdleCallback`), und
   * die haben hier nichts aufzuschieben. Ohne Kennung gibt es nichts zu holen.
   *
   * `ProjectPage` ist der EINZIGE Anmelder — die Regel dazu steht am Hook.
   */
  //
  // Geschlossen ist nicht gleich `none`: Mit Elementen meldet das Panel den
  // **Canvas-Anriss** (`peek`), und auch dem weicht die Breadcrumb. Solange
  // die Elemente noch laden, ist nicht entschieden, welche der beiden Größen
  // kommt — dann wartet der Schirm auch darauf (`canvasNotesPending`), sonst
  // spränge die Zeile aus dem Fluss, sobald sie eintreffen.
  // Der Teaser weicht, sobald der Canvas offen ist: er lädt ja gerade dazu
  // ein, ihn zu öffnen — beides nebeneinander wäre widersinnig, und ohne das
  // bliebe das Panel bei leerem Projekt ungerendert (siehe
  // `shouldRenderCanvasPanel`), das Kürzel liefe also ins Leere.
  // `isMorphing`: Nach dem Bestätigen ist der Ersteller schon zu, die Aufgabe
  // aber noch nicht über `useLiveQuery` da. In dieser Lücke schlug der Teaser
  // zu und hängte das Brett samt laufendem Morph aus — die Attrappe blieb
  // danach für immer stehen (`.claude/tasks/empty-state-geisterzeile.md`).
  const isMorphing = morphData !== null || positionMorphKey !== null
  const listedSections = useMemo(
    () => effectiveSections?.filter(section => !section.archivedAt),
    [effectiveSections],
  )
  const showEmptyStateTeaser = shouldShowCanvasEmptyState(
    effectiveTasks, canvasNotes, isCreatingTask, canvasIsOpen, isMorphing, listedSections
  )

  /**
   * **Ob die Spalte überhaupt dastehen darf.** EINE Größe für den Knopf, für
   * das Markup und für die CSS-Variable, und das ist der Punkt: `aria-pressed`
   * ist damit genau dann wahr, wenn wirklich Aufgaben-Details zu sehen sind.
   * Aus zwei getrennt gerechneten Bedingungen wurde im gepackten Programm ein
   * eingerasteter Knopf neben nichts (2026-09-12).
   *
   * Der **Teaser des leeren Projekts** zählt seit #220 NICHT mehr mit: Ohne
   * Aufgaben zeigen die Details das Projekt, der Knopf steht also auch dort
   * (entschieden 29.09.2026). Wird die letzte Aufgabe unter der offenen Karte
   * gelöscht, bleibt sie offen und zeigt das Projekt.
   *
   * Alles Weitere — offen/zu, der Abgang, wen sie zeigen — liegt in
   * `useTaskDetailsPane` (herausgezogen 21.09.2026, F-KN4/F-DN3), weil **Kurz
   * notiert** und **Demnächst** dieselbe Spalte bekommen.
   */
  const detailsPaneAllowed = detailsAllowed



  // ── Task operations (edit, complete, delete) ──
  // Wired with the optimistic-override sink so toggle / inline-edit /
  // delete paint in the next paint instead of waiting for the IDB write
  // → LiveQuery roundtrip. We pass `effectiveTasks` (override-applied)
  // so a rapid second click reads the *intended* status — not the still-
  // stale raw Dexie row that hasn't seen the LiveQuery refire yet.
  const { handleUpdateTask, handleToggleComplete, handleDeleteTask } = useTaskOperations({
    t, setOptimisticTaskOverride, tasks: effectiveTasks,
  })

  // Jeder Weg zum Haken läuft hier durch — der Klick auf das Kästchen und die
  // `Leertaste` auf der ganzen Auswahl. Erst die **Liegezeit** merken, dann
  // schalten: Sonst wäre die Zeile im nächsten Bild schon hinter dem Schalter.
  const toggleCompleteWithGrace = useCallback((ids: Set<string>) => {
    markCompleted(ids)
    handleToggleComplete(ids)
  }, [markCompleted, handleToggleComplete])

  // ── Cmd/Ctrl+Enter in expanded mode: save edit + open creator right after task ──
  const handleUpdateTaskAndCreateBelow = useCallback(async (taskId: string, data: TaskCreationData) => {
    await handleUpdateTask(taskId, data)
    startCreatingAfter(taskId)
  }, [handleUpdateTask, startCreatingAfter])

  // ── Section operations ──
  const {
    autoEditSectionId,
    handleAddSection,
    handleToggleCollapse,
    handleRenameSection,
    handleDeleteSection,
    handleArchiveSection,
    handleUnarchiveSection,
    pendingArchive,
    confirmArchive,
    cancelArchive,
    toggleCollapseRef,
  } = useProjectSections({
    projectId, tasks: effectiveTasks, sections: effectiveSections, segments,
    setOptimisticSectionOverride, setOptimisticTaskOverride,
  })

  /*
    **Was hinter dem Geschafft-Schalter liegt, gehört zur Navigation — wenn er offen ist.**

    Die Zeilen dort sind gewöhnliche Aufgabenzeilen (CONTEXT.md, Geschafft-Schalter), also
    laufen Pfeiltasten hinein, `⇧`/`⌘` wählen über die Grenze und `Entf` trifft sie. Ein
    EIGENER Satz Segmente, nicht `segments`: Die gehören dem Zeichnen und dem Ziehen, und
    dort hat der Haufen nichts verloren — er steht außerhalb des `SortableContext`.

    Zugeklappt fehlt er auch hier: Was man nicht sieht, kann man nicht meinen.
  */
  const navSegments = useMemo<RenderSegment[]>(
    () => (completedOpen && completedTasks.length > 0
      ? [...segments, { section: null, tasks: completedTasks, taskCount: completedTasks.length }]
      : segments),
    [segments, completedOpen, completedTasks],
  )

  // ── Unified cross-section keyboard navigation ──
  // ⌥↑/↓ und ⇧⌥↑/↓ (#204): Die Navigation entsteht vor den Wegen, die sie selbst braucht
  // (Aufklappen, Umsortieren) — deshalb über Refs, gesetzt weiter unten.
  const moveSelectionRef = useRef<((ids: string[], direction: -1 | 1) => void) | null>(null)
  const nestSelectionRef = useRef<((ids: string[], parentId: string, direction: -1 | 1) => void) | null>(null)
  const handleMoveSelection = useCallback((ids: string[], direction: -1 | 1) => moveSelectionRef.current?.(ids, direction), [])
  const handleNestSelection = useCallback((ids: string[], parentId: string, direction: -1 | 1) => nestSelectionRef.current?.(ids, parentId, direction), [])
  const navigation = useProjectNavigation({
    onMoveSelection: handleMoveSelection,
    onNestSelection: handleNestSelection,
    segments: navSegments,
    onEscapeTop: () => {
      setIsEditingHeader(true)
      requestAnimationFrame(() => {
        (notesRef.current ?? titleRef.current)?.focus()
      })
    },
    onDelete: handleDeleteTask,
    onDeleteSection: handleDeleteSection,
    onToggleComplete: toggleCompleteWithGrace,
    onToggleCollapse: (sectionId: string) => toggleCollapseRef.current?.(sectionId),
    onCreateBelow: startCreatingAfter,
    onCreateInSection: (sectionId: string) => {
      const segIdx = visibleSegments.findIndex(s => s.section?.id === sectionId)
      if (segIdx < 0) return
      activeSegmentRef.current = segIdx
      startCreating('top')
    },
    isDragAnimatingRef,
    skipFocusCorrectionRef: pendingSelectionRestoreRef,
    isActive,
    /*
      **Bei offenen Aufgaben-Details klappt nichts mehr auf** (CONTEXT.md): Die
      Aufgabe steht dort vollständig, und die aufgeklappte Karte hielte Titel
      und Notizen als zweiten Entwurf desselben Satzes.

      Der Doppelklick bleibt trotzdem, was er ist — nur der Ort wechselt: Er
      wählt die Zeile und setzt den Caret in den Titel der
      Aufgaben-Details.
    */
    suppressExpand: () => detailsRef.current?.open ?? false,
    onSuppressedExpand: () => detailsRef.current?.signalFocusTitle(),
  })
  // Destructure stable useCallback references for dependency arrays
  const { clearSelection: navClearSelection, selectTask: navSelectTask } = navigation
  navFocusFirstRef.current = navigation.focusFirst
  navClearSelectionRef.current = navClearSelection
  navSelectTaskRef.current = navSelectTask
  navSelectSectionRef.current = navigation.selectSection

  /*
    **Die Aufgaben-Details dieser Seite** (`useTaskDetailsPane`, herausgezogen
    21.09.2026): offen/zu, wen sie zeigen, der Abgang. Er steht HIER und nicht
    weiter oben, weil er die Auswahl der Navigation braucht — und die entsteht
    eine Zeile vorher.
  */
  const details = useTaskDetailsPane({
    tasks: effectiveTasks,
    allowed: detailsPaneAllowed,
    project,
    navigation: {
      selectedTaskIds: navigation.selectedIds,
      selectTask: navigation.selectTask,
      stepTask: navigation.stepToAdjacentTask,
      canStep: direction => navigation.adjacentTaskIndex(direction) !== -1,
      focusList: () => navigation.wrapperRef.current?.focus({ preventScroll: true }),
      topVisibleTaskId: () => {
        const container = bodyMainRef.current
        return container ? topVisibleTaskId(container) : null
      },
      sectionFocused: navigation.focusedSectionId != null,
    },
    setOptimisticTaskOverride,
    // Neben den Details so breit wie neben dem Canvas — sonst verschwänden im Kopf die Buttons (#296).
    listMinWidth: LIST_MIN_WIDTH,
  })
  detailsRef.current = details

  /**
   * **Die Seite geht mit dem Projekt mit** (#220): Nach dem Umzug in einen anderen Lebensbereich —
   * und nach dessen Rückgängig — zieht `MainLayout` diesen Tab und die App dorthin. Sonst stünde man
   * auf einer Seite, deren Projekt in der Seitenleiste fehlt. Über ein Ereignis, weil nur
   * `MainLayout` den Tab samt seinem Lebensbereich umsetzen kann (`navigateActiveTab`).
   */
  const handleMovedToLifeSpace = useCallback((target: LifeSpace) => {
    if (!projectId) return
    dispatchAppEvent('project-moved-life-space', { projectId, lifeSpace: target })
  }, [projectId])
  const detailsOpen = details.open
  const detailsVisible = details.visible

  /**
   * **Canvas und Aufgaben-Details sind nie beide zu sehen.**
   *
   * Der Canvas schließt sie beim Aufgehen (die Rangfolge des Konzepts). Wer sie
   * andersherum bei OFFENEM Canvas aufmacht, bekam bisher drei Karten
   * nebeneinander — die Fläche reichte für keine davon. Stattdessen legt sich
   * die Karte jetzt GENAU auf den Canvas: dieselbe Breite, dieselbe Rechnung
   * (`--active-canvas-split-ratio`), nur eine Ebene höher. Die Aufgabenliste
   * bleibt dabei stehen, wo sie steht — es verschiebt sich nichts, es kommt
   * nur etwas darüber.
   *
   * Der Canvas darunter wird zusätzlich unsichtbar geschaltet: Zwei Karten mit
   * verschiedenen Eckradien decken sich an den Ecken nie ganz, und dort blitzte
   * er sonst hervor. Sein Zustand bleibt dabei stehen — er ist nur verdeckt,
   * nicht zu.
   *
   * Maximiert ist der Fall nicht erreichbar: Der Knopf sitzt im Kopf der
   * Aufgabenliste, und die ist dann aus dem Bild gefahren.
   */
  const detailsOverCanvas = detailsVisible && canvasIsOpen


  // Mit offenen Aufgaben-Details ist der Anriss unterdrückt (siehe
  // `peekSuppressed` am Panel) — die erwartete Größe muss dasselbe sagen,
  // sonst wartete der Logo-Schirm auf eine Größe, die nie gemeldet wird.
  const expectedCanvasMode = !showCanvasPanel
    ? 'none'
    : canvasPanelState === 'closed'
      ? (!detailsVisible && shouldShowCanvasPeek('closed', canvasNotes) ? 'peek' : 'none')
      : canvasPanelState
  const canvasGeometrySettled = canvasMode === expectedCanvasMode
  const canvasNotesPending = showCanvasPanel && canvasPanelState === 'closed' && canvasNotes === undefined

  useWindowRevealHold(
    isActive
    && !!projectId
    && (restoredCanvasStateFor !== projectId || !canvasGeometrySettled || canvasNotesPending)
  )

  /**
   * Mehrere auf einmal — der Knopf „Anzeigen" im Hinweis nach einer
   * **Übernahme**. Ausgewählt werden alle, ins Bild gerückt die erste.
   *
   * Die Auswahl direkt über `setSelectedIds` und nicht Zeile für Zeile: Sie
   * sollen zusammen erscheinen, als das Ergebnis EINER Handlung.
   */
  const handleRevealTasks = useCallback((taskIds: string[], options?: { openList?: boolean }) => {
    if (taskIds.length === 0) return

    /*
      Bei **maximiertem** Canvas ist die Aufgabenliste nach links aus dem Bild
      gefahren. Dann gilt: NUR auf ausdrückliche Bitte hin zurückholen.

      Das trennt die beiden Aufrufer. Die **Übernahme** ruft von selbst — sie
      soll zeigen, wo die Aufgabe gelandet ist, aber niemandem die maximierte
      Fläche unter den Händen wegziehen. Der Knopf „Anzeigen" im Hinweis ruft
      mit `openList` — wer ihn drückt, will genau dorthin.
    */
    if (canvasPanelState === 'maximized') {
      if (!options?.openList) return
      changeCanvasPanelState('split')
    }

    navigation.setSelectedIds(new Set(taskIds))
    revealTask(taskIds[0]!)
  }, [canvasPanelState, changeCanvasPanelState, revealTask, navigation])

  /**
   * Der Rückweg aus dem **Geschafft-Schalter**: Haken heraus, und die Aufgabe
   * steht wieder an ihrem Platz — ausgewählt und ins Bild geholt.
   *
   * Kein `markCompleted`: Die **Liegezeit** gilt dem Abhaken, nicht dem
   * Zurücknehmen. Und liegt die Aufgabe in einem eingeklappten Abschnitt,
   * klappt der auf — sonst führte das Zurückholen ins Unsichtbare.
   *
   * `revealTask` sucht die Zeile bis zu eine Sekunde lang je Bild: Sie kommt
   * erst mit dem nächsten Anstrich (optimistische Überschreibung) bis zur
   * Antwort aus Dexie.
   */
  // Jedes Mal frisch: `revealTaskInList` bleibt so stabil, und sein Aufräumer bricht keinen Sprung ab.
  openCollapsedSectionOfRef.current = (taskId: string) => {
    const sectionId = collapsedSectionOf(taskId, effectiveTasks, effectiveSections)
    if (sectionId) toggleCollapseRef.current?.(sectionId)
  }

  // Die Elternaufgabe einer eingehängten Aufgabe zeigen: Geschafft-Schalter und Abschnitt auf,
  // dann auswählen und aufklappen (`revealSubtask`).
  const { setEditingTaskId: setEditingForReveal } = navigation
  revealNestedRef.current = (parentId, subtaskId) => {
    const parent = effectiveTasks?.find(item => item.id === parentId)
    const section = parent?.sectionId ? effectiveSections?.find(item => item.id === parent.sectionId) : undefined
    if (section?.collapsed) toggleCollapseRef.current?.(section.id)
    if (completedIdsRef.current.has(parentId)) setCompletedOpen(true)
    return revealSubtask({ parentId, subtaskId, select: navSelectTask, expand: setEditingForReveal })
  }

  const handleRestoreCompleted = useCallback((taskId: string) => {
    const task = effectiveTasks?.find(item => item.id === taskId)
    const section = task?.sectionId
      ? effectiveSections?.find(item => item.id === task.sectionId)
      : undefined
    if (section?.collapsed) toggleCollapseRef.current?.(section.id)
    handleToggleComplete(new Set([taskId]))
    revealTask(taskId)
  }, [effectiveTasks, effectiveSections, toggleCollapseRef, handleToggleComplete, revealTask])

  // ── Focus the task-list wrapper whenever this page becomes active ──
  // TaskList's arrow-key handler is bound to its own element (onKeyDown via
  // listProps) and `navigation.onKeyDown` to the wrapper. With TabStack the
  // page gets `display: none` when inactive, which drops the browser's
  // focused element onto <body>. Without this effect the user had to click
  // the list once after every tab switch to get keyboard nav back.
  useEffect(() => {
    if (!isActive) return
    // Wait one frame — on the first activation the wrapper div may not yet
    // be in the DOM (loading skeleton branch still rendering).
    const rafId = requestAnimationFrame(() => {
      // Don't steal focus if the user is already typing somewhere (e.g. in
      // the project title input, which mounts before the wrapper).
      const active = document.activeElement
      const typingTag = active?.tagName
      if (typingTag === 'INPUT' || typingTag === 'TEXTAREA') return
      navigation.wrapperRef.current?.focus({ preventScroll: true })
    })
    return () => cancelAnimationFrame(rafId)
  }, [isActive, navigation.wrapperRef])
  const focusedItem = navigation.focusedNavIndex !== null ? navigation.flatNavList[navigation.focusedNavIndex] : null
  navFocusedItemRef.current = focusedItem ? { type: focusedItem.type, id: focusedItem.id } : null

  // ── Selection handover across the list ↔ stack switch ──
  const { selectedTaskId: stackEntryTaskId, onActiveTaskChange: handleStackActiveTaskChange } =
    useStackModeSelection({
      isStackMode,
      focusedNavIndex: navigation.focusedNavIndex,
      flatNavList: navigation.flatNavList,
      selectedIds: navigation.selectedIds,
      selectTask: navSelectTask,
    })

  // ── Selection save/restore across tab switches ──
  useSelectionCache({
    projectId, navigation, pendingRestoreRef, pendingSelectionRestoreRef,
    correctDataReady, selectionCacheRef,
  })

  // Derive which segment is "active" — target for A/B/F, ⌘N and „Übernehmen"
  // In den Indizes von `visibleSegments`: Darin zeichnet `TaskBoard` den Ersteller und meldet den Hover, darin
  // speichert `useTaskCreation`. Zeigt die Liste nur einen Abschnitt, läge ein voller Index daneben (#274).
  const activeSegmentIndex = useMemo(() => {
    // 1. Selection has priority — when a task/section is selected, it is the target
    if (navigation.focusedNavIndex !== null) {
      const focusedItem = navigation.flatNavList[navigation.focusedNavIndex]
      if (focusedItem) {
        const segIdx = visibleSegments.findIndex(seg =>
          focusedItem.type === 'section'
            ? seg.section?.id === focusedItem.id
            : seg.tasks.some(t => t.id === focusedItem.id)
        )
        if (segIdx !== -1) return segIdx
      }
    }
    // 2. No selection — hover determines position
    if (hoveredSegmentIndex !== null && hoveredSegmentIndex < visibleSegments.length) {
      return hoveredSegmentIndex
    }
    // 3. Fallback: last interacted segment
    return Math.min(lastInteractedSegmentIndex, visibleSegments.length - 1)
  }, [hoveredSegmentIndex, navigation.focusedNavIndex, navigation.flatNavList, visibleSegments, lastInteractedSegmentIndex])

  // Keep ref in sync so callbacks defined before useMemo can read the latest value
  activeSegmentRef.current = activeSegmentIndex

  /*
    **Übernehmen** — aus ausgewählten Canvas-Elementen werden Aufgaben.

    Der Zielabschnitt ist der zuletzt benutzte, derselbe, an dem auch `B`
    anlegt (CONTEXT.md, „Übernehmen"). `visibleSegments` liefert
    ihn über `activeSegmentIndex`; ein Index daneben (leeres Projekt, gerade
    gelöschter Abschnitt) bedeutet schlicht den Block oben.
  */
  const applyCanvasNotes = useApplyCanvasNotes({
    projectId,
    lifeSpace,
    targetSectionId: visibleSegments[activeSegmentIndex]?.section?.id ?? null,
    tasks: effectiveTasks,
    sections: effectiveSections,
    revealTasks: handleRevealTasks,
  })

  // Track last interacted segment from keyboard navigation
  useEffect(() => {
    if (navigation.focusedNavIndex === null) return
    const focusedItem = navigation.flatNavList[navigation.focusedNavIndex]
    if (!focusedItem) return
    const segIdx = visibleSegments.findIndex(seg =>
      focusedItem.type === 'section'
        ? seg.section?.id === focusedItem.id
        : seg.tasks.some(t => t.id === focusedItem.id)
    )
    if (segIdx !== -1) setLastInteractedSegmentIndex(segIdx)
  }, [navigation.focusedNavIndex, navigation.flatNavList, visibleSegments])

  /*
    Die **Listen-Werkzeug-Pille** (#229). „Neue Aufgabe" legt immer oben an
    (#272, `listToolbarTarget.ts`): im Abschnitt der markierten Zeile, sonst ganz
    oben ohne Abschnitt. ⌘N und das Menü nehmen denselben Weg. Für „Abschnitt"
    misst `listToolbarTargetInput` im Moment des Klicks die Abschnitte im Bild.

    Gerechnet auf `visibleSegments` — in dessen Indizes zeichnet `TaskBoard`
    den Ersteller, wie zuvor beim Plus (`AddTask`) am Segment.
  */
  const listToolbarTargetInput = useCallback(() => {
    const focused = navFocusedItemRef.current
    const scroller = bodyMainRef.current
    return {
      segments: visibleSegments,
      focusedTaskId: focused?.type === 'task' ? focused.id : null,
      visibleSectionKeys: scroller ? measureVisibleSectionKeys(scroller, LIST_TOOLBAR_CLEARANCE, LIST_TOP_FADE) : [],
    }
  }, [visibleSegments])

  const handleListToolbarAddTask = useCallback(() => {
    const { position, segmentIndex, expandSectionId } = resolveNewTaskTarget({
      segments: visibleSegments, focused: navFocusedItemRef.current, singleSection: showsSingleSection,
    })
    // Eingeklappt landete die neue Aufgabe unsichtbar.
    if (expandSectionId) void handleToggleCollapse(expandSectionId)
    startCreating(position, segmentIndex)
  }, [visibleSegments, showsSingleSection, handleToggleCollapse, startCreating])

  /*
    **„Neue Aufgabe“ aus der Pille in die Liste ziehen** (#301). Jede Lücke zwischen Zeilen ist ein
    Ziel, dazu der Kopf eines zugeklappten Abschnitts („ans Ende“). Angelegt wird über dieselben Wege
    wie per Tastatur; die Eingabe geht in der Höhe der Lücke auf.
  */
  const handleNewTaskDrop = useCallback((target: NewTaskDropTarget) => applyNewTaskDrop(target, {
    startCreating, startCreatingAfter, expand: sectionId => { void handleToggleCollapse(sectionId) },
  }), [startCreating, startCreatingAfter, handleToggleCollapse])
  const acceptsNewTask = useCallback(() => 'all' as const, [])
  const newTaskDrag = useNewTaskDrag({
    isActive,
    scrollerRef: bodyMainRef,
    listRef: taskListRef,
    accepts: acceptsNewTask,
    onDrop: handleNewTaskDrop,
    edgeInsets: { top: 0, bottom: LIST_TOOLBAR_CLEARANCE },
  })
  // Nach einem Zug kommt der Klick des Buttons noch an — er legt nicht ein zweites Mal an.
  const { consumeClick: consumeNewTaskClick } = newTaskDrag
  const handleListToolbarAddTaskClick = useCallback(() => {
    if (!consumeNewTaskClick()) handleListToolbarAddTask()
  }, [consumeNewTaskClick, handleListToolbarAddTask])

  /*
    Neuer Abschnitt — `⇧A` darüber, `⇧B` darunter (wie der Knopf der Pille
    und das Menü): Sind mindestens zwei Aufgaben markiert, kommen sie mit
    hinein; eine einzelne markierte Zeile ist nur der Cursor.

    `⇧F` macht aus den obersten Aufgaben eine Gruppe: ganz oben, mit allen
    Aufgaben ohne Abschnitt (auch erledigten, die gerade nicht zu sehen sind);
    gibt es keine, leer vor dem ersten Abschnitt. Die Markierung zählt dort
    nicht. Über einem Abschnitt kann keine Aufgabe ohne Abschnitt stehen — die
    Liste ordnet nach `order`, alles unter einem Kopf gehört zu ihm.
  */
  const handleAddSectionAt = useCallback((where: NewSectionWhere) => {
    const { segments: listed, visibleSectionKeys } = listToolbarTargetInput()
    let packIds: string[]
    if (where === 'top') {
      const firstSectionOrder = Math.min(...(effectiveSections ?? []).map(section => section.order))
      packIds = (effectiveTasks ?? [])
        .filter(task => !task.parentTaskId && task.order < firstSectionOrder)
        .map(task => task.id)
    } else {
      const visibleTaskIds = new Set(listed.flatMap(segment => segment.tasks.map(task => task.id)))
      const marked = [...navigation.selectedIds].filter(id => visibleTaskIds.has(id))
      packIds = marked.length >= 2 ? marked : []
    }
    const { anchorId, placement, position } = resolveNewSectionPlacement({
      segments: listed, focused: navFocusedItemRef.current, visibleSectionKeys, packIds, where,
    })
    void handleAddSection(position ?? 'bottom', anchorId, placement, packIds)
  }, [listToolbarTargetInput, effectiveSections, effectiveTasks, navigation.selectedIds, handleAddSection])

  /*
    **„Abschnitt“ aus der Pille in die Liste ziehen** (#314). Dieselben Lücken wie bei der Aufgabe; am
    zugeklappten Kopf heißt es „danach“. Der neue Abschnitt nimmt alles bis zum nächsten Kopf mit
    (`sectionCreationForDropTarget`), sein Titel geht zur Eingabe auf, und ohne Namen gehen die Aufgaben zurück
    (`handleAddSection`). Lücke und Fläche halten den Platz, bis der neue Kopf in der Liste steht.
  */
  const [droppedSectionId, setDroppedSectionId] = useState<string | null>(null)
  // Nur der jüngste Zug meldet seinen Kopf — einer, dessen Wartezeit schon abgelaufen ist, landete sonst den nächsten.
  const [sectionDropGate] = useState(createLatestGate)
  const handleNewSectionDrop = useCallback((target: NewTaskDropTarget) => {
    const creation = sectionCreationForDropTarget({
      target,
      segmentSectionId: visibleSegments[target.segmentIndex]?.section?.id ?? null,
      tasks: effectiveTasks ?? [],
      sections: effectiveSections ?? [],
    })
    if (!creation) return false
    setDroppedSectionId(null)
    void handleAddSection(
      'anchorId' in creation ? 'bottom' : creation.position,
      'anchorId' in creation ? creation.anchorId : undefined,
      'anchorId' in creation ? creation.placement : undefined,
      creation.packTaskIds,
      sectionDropGate.next(setDroppedSectionId),
    )
    return true
  }, [visibleSegments, effectiveTasks, effectiveSections, handleAddSection, sectionDropGate])
  const sectionLanded = droppedSectionId !== null && visibleSegments.some(segment => segment.section?.id === droppedSectionId)
  useEffect(() => {
    if (!sectionLanded) return
    // Gelandet ist im selben Bild (`useNewTaskDrag`); danach ist die Kennung vergessen.
    const timer = setTimeout(() => setDroppedSectionId(null), 0)
    return () => clearTimeout(timer)
  }, [sectionLanded])
  const acceptsNewSectionAt = useCallback((index: number) => acceptsNewSection(visibleSegments[index]), [visibleSegments])
  const newSectionDrag = useNewTaskDrag({
    kind: 'section',
    landed: sectionLanded,
    landingSectionId: droppedSectionId,
    isActive,
    scrollerRef: bodyMainRef,
    listRef: taskListRef,
    accepts: acceptsNewSectionAt,
    onDrop: handleNewSectionDrop,
    edgeInsets: { top: 0, bottom: LIST_TOOLBAR_CLEARANCE },
  })
  const { consumeClick: consumeNewSectionClick } = newSectionDrag
  const handleListToolbarAddSection = useCallback(() => {
    if (!consumeNewSectionClick()) handleAddSectionAt('below')
  }, [consumeNewSectionClick, handleAddSectionAt])

  /*
    Ein frischer Abschnitt zählt schon ab dem Anlegen (`autoEditSectionId`),
    nicht erst, wenn sein Kopf sich meldet — sonst stand die Pille noch einen
    Moment da, und auf der leeren Seite erschien sie und tauchte sofort ab.
  */
  const listToolbarRetracted = isListToolbarRetracted({
    creatingTask: isCreatingTask,
    namingSection: namingSectionId !== null || autoEditSectionId !== null,
    placingPaper: canvasPlacing && placingFromList,
    // Vom Anfassen bis die Fläche weg ist — beim Abschnitt auch, während er auf seinen Kopf wartet.
    draggingFromPill: newTaskDrag.drop !== null || newSectionDrag.drop !== null,
  })

  const handleListToolbarPlaceNote = useCallback(() => requestCanvasPlacement('note'), [requestCanvasPlacement])
  const handleListToolbarPlaceMemo = useCallback(() => requestCanvasPlacement('memo'), [requestCanvasPlacement])
  /*
    Ein **Zug** von Notiz/Memo trägt den Zettel in die Pinnwand. Scharf wird
    erst jenseits der Schwelle, nicht schon beim Anfassen wie an der
    Werkzeug-Pille: Sonst ginge die Pinnwand unter der noch gedrückten Maus
    auf — im leeren Projekt maximiert, und das Loslassen träfe die Fläche.
  */
  const handleListToolbarNotePointerDown = useCallback((event: React.PointerEvent) => {
    watchPlacementDrag({ x: event.clientX, y: event.clientY }, () => requestCanvasPlacement('note', true))
  }, [requestCanvasPlacement])
  const handleListToolbarMemoPointerDown = useCallback((event: React.PointerEvent) => {
    watchPlacementDrag({ x: event.clientX, y: event.clientY }, () => requestCanvasPlacement('memo', true))
  }, [requestCanvasPlacement])

  // ── Drag-and-drop ──
  // Herauslösen (#204): Die neue Aufgabe wird ausgewählt, sobald sie in der Liste steht.
  const navVisibleIds = useMemo(() => navigation.flatNavList.map(item => item.id), [navigation.flatNavList])
  // Herausgelöst (Ziehen oder ⇧⌥): die Aufgaben in der Liste ausgewählt, sobald sie dort stehen.
  const { setSelectedIds: setNavSelectedIds } = navigation
  const markPendingSelection = usePendingSelection(navVisibleIds, ids => {
    navSelectTask(ids[0]!)
    if (ids.length > 1) setNavSelectedIds(new Set(ids))
  })
  // Einhängen und Umhängen (#204): Das Ziel klappt auf, man sieht die Unteraufgabe dort landen.
  const { setEditingTaskId: setEditingForNest } = navigation
  const handleExpandForNest = useCallback((taskId: string) => {
    navSelectTask(taskId)
    setEditingForNest(taskId)
  }, [navSelectTask, setEditingForNest])
  const {
    activeDragId, draggedIds, dragCount, isCrossSegmentDrag, gapAfterSegmentIdx, gapHeight,
    crossSegGapAfterRef, crossSegGapAfter, nestTarget, nestLatchRef, subtaskDragOut, insertLine, leaveList,
    sensors, handleDragStart, handleDragMove, handleDragOver, handleDragEnd, handleDragCancel,
  } = useTaskDragAndDrop({
    segments, tasks, sections, taskListRef, navSelectTask, setOptimisticUpdates,
    onExpandTask: handleExpandForNest,
    onSelectTasks: markPendingSelection,
  })
  // Per Tastatur und Menü verlässt die Zeile die Liste wie beim Ziehen: Ihre Stelle fährt zu.
  const { nest: nestByCommand } = useNestActions({ onExpandTask: handleExpandForNest, onLeave: leaveList })
  // ⇧⌥↑ hängt hinten in die Aufgabe darüber (man kommt von unten), ⇧⌥↓ vorn in die darunter.
  nestSelectionRef.current = (ids, parentId, direction) => { void nestByCommand(ids, parentId, { at: direction === -1 ? 'end' : 'start' }) }
  moveSelectionRef.current = useKeyboardMove({ segments, setOptimisticUpdates, crossSections: true })
  const nestMenu = useNestMenu({
    segments, selectedIds: navigation.selectedIds, nest: nestByCommand, untitled: t('taskList.nest.untitled'),
    selectInList: markPendingSelection,
  })
  // ── Section drag-and-drop ──
  const {
    activeSectionDragId,
    hoverTargetIdx,
    dropAnimRef: sectionDropAnimRef,
    segmentRectsRef,
    sourceHeaderHeightRef,
    sourceHeaderTopRef,
    handleSectionDragStart,
    handleSectionDragOver,
    handleSectionDragEnd,
    handleSectionDragCancel,
  } = useSectionDrag({ segments, tasks, sections, wrapperRef: taskListRef, setOptimisticUpdates, setOptimisticSectionUpdates })

  // Section drag animations: counter-transforms, swap, FLIP settle
  useSectionDragAnimation({
    wrapperRef: taskListRef,
    activeSectionDragId,
    hoverTargetIdx,
    segments,
    dropAnimRef: sectionDropAnimRef,
    segmentRectsRef,
    sourceHeaderHeightRef,
  })

  // Clear optimistic section updates after section drag completes.
  // Delayed so useLiveQuery has time to pick up ALL Dexie writes (sections + tasks).
  // Section reorder can touch many items (25+ tasks), so allow more time than task drag.
  useEffect(() => {
    if (!activeSectionDragId) {
      const timer = setTimeout(() => {
        setOptimisticSectionUpdates(() => null)
      }, 300)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [activeSectionDragId])

  isDragAnimatingRef.current = activeDragId !== null || activeSectionDragId !== null

  // Dynamic overlay Y offset: measure how much the source header shifted after CSS changes.
  // Replaces the fixed SECTION_DRAG_OVERLAY_OFFSET for section drags.
  const [sectionDragYOffset, setSectionDragYOffset] = useState(0)
  useLayoutEffect(() => {
    if (!activeSectionDragId) {
      setSectionDragYOffset(0)
      return
    }
    const headerEl = document.querySelector<HTMLElement>(`.section-header[data-section-id="${activeSectionDragId}"]`)
    if (headerEl) {
      const postCssTop = headerEl.getBoundingClientRect().top
      setSectionDragYOffset(postCssTop - sourceHeaderTopRef.current)
    }
  }, [activeSectionDragId, sourceHeaderTopRef])

  // ── Composed DnD orchestration (section + task drag) ──
  const excludeId = (morphData || inlineMorphTitle) ? autoSelectId ?? undefined : undefined
  const {
    composedDragStart, composedDragMove, composedDragOver, composedDragEnd, composedDragCancel,
    customCollisionDetection, allSortableIds, segmentAwareStrategy,
    sectionDragWidth, dragRotation, dragSourceSegIdx,
  } = useDndComposition({
    segments, excludeId,
    handleSectionDragStart, handleSectionDragOver, handleSectionDragEnd, handleSectionDragCancel,
    activeSectionDragId,
    handleDragStart, handleDragMove, handleDragOver, handleDragEnd, handleDragCancel,
    crossSegGapAfterRef, crossSegGapAfter, nestLatchRef,
    onDragFinish: navigation.restoreListFocus,
  })

  const handleHoverLeave = useCallback(() => {
    setHoveredSegmentIndex(null)
  }, [])

  // ── Duplicate items (Cmd/Ctrl+D) ──
  // Pass `effectiveTasks` (override-merged) so that a duplicate fired
  // immediately after a status-toggle clones the toggled state — not the
  // stale Dexie row that's still mid-LiveQuery-refire.
  const { handleDuplicate } = useDuplicateItems({
    segments, tasks: effectiveTasks, sections,
    selectedIds: navigation.selectedIds,
    flatNavList: navigation.flatNavList,
    focusedNavIndex: navigation.focusedNavIndex,
    setSelectedIds: navigation.setSelectedIds,
    setFocusedNavIndexWithAnchor: navigation.setFocusedNavIndexWithAnchor,
  })

  // Right-click "Duplicate": if the clicked task is part of a multi-selection,
  // duplicate all selected tasks (matches Delete behaviour). Otherwise duplicate
  // just the clicked task, bypassing the selection state entirely to avoid
  // timing issues with async selection updates from handleContextMenu.
  const handleDuplicateFromContextMenu = useCallback((taskId: string) => {
    const selected = navigation.selectedIds
    if (selected.size > 1 && selected.has(taskId)) {
      handleDuplicate()
    } else {
      handleDuplicate([taskId])
    }
  }, [handleDuplicate, navigation.selectedIds])

  const handleContextMenuBadgeUpdate = useContextMenuBadgeUpdate({
    getSelection: () => navigation.selectedIds,
    setOptimisticTaskOverride,
    logTag: 'ProjectPage',
  })

  /* Die Haken im **Aufgaben-Menü** — dieselbe Auswahl wie oben. */
  const getContextMenuMarks = useContextMenuMarks({
    getSelection: () => navigation.selectedIds,
    tasks: tasks ?? [],
  })

  // ── Die Aufgaben-Details ──



  /** **Play** — beginnt einen Fokus an dieser Aufgabe (Zeile, Menü, Details). */
  const handlePlayTask = usePlayTask()



  // Wer den Canvas öffnet, schließt die Aufgaben-Details — beim Öffnen wie beim
  // Maximieren. Der Canvas ist die große Fläche; wer ihn aufmacht, will
  // dorthin. Seit es das Slide-over nicht mehr gibt, ist das die EINZIGE Regel
  // zwischen beiden: Sie teilen sich denselben Platz und können nicht
  // gleichzeitig dastehen.
  useEffect(() => {
    if (canvasPanelState === 'closed') return
    /*
      Ohne Abgang: Der Canvas fährt gerade selbst auf, und zwei Bewegungen
      übereinander liest niemand. Genau dafür hat der Griff
      `closeWithoutMotion` — und er merkt sich selbst, ob überhaupt etwas
      offen stand.

      Über den Ref, weil der Griff sonst in den Abhängigkeiten stünde und
      jedes Öffnen bei offenem Canvas sofort wieder zumachte.
    */
    detailsRef.current?.closeWithoutMotion()
  }, [canvasPanelState])

  // Eine bereits offene Karte klappt zu, sobald die Aufgaben-Details aufgehen:
  // Zwei offene Editoren wären zwei Entwürfe desselben Satzes.
  const { setEditingTaskId: navSetEditingTaskId } = navigation
  useEffect(() => {
    if (detailsOpen) navSetEditingTaskId(null)
  }, [detailsOpen, navSetEditingTaskId])

  /*
    **Die Kürzel des Aufgaben-Menüs** öffnen denselben Kasten wie die Menüzeile,
    verankert an der Zeile, auf der der Fokus steht. Das Ziel steht hier, weil
    die Zeile selbst nicht weiß, dass irgendwo eine Taste gedrückt wurde.
  */
  const [menuShortcut, setMenuShortcut] = useState<{ taskId: string; target: TaskMenuTarget } | null>(null)
  const closeMenuShortcut = useCallback(() => setMenuShortcut(null), [])
  // „Verschieben …“ aus dem Rechtsklick öffnet denselben Kasten an der Zeile wie ⇧⌘M (#263).
  const openMoveAtRow = useCallback((taskId: string) => setMenuShortcut({ taskId, target: 'move' }), [])

  /*
    `R` geht ohne Kasten: Es legt die Aufgabe schlafen oder weckt sie, über
    denselben Kanal wie der Rechtsklick — und damit auf die ganze AUSWAHL.
    Welche Richtung gemeint ist, sagt die Zeile unter dem Fokus: Solange sie
    wach ist, wird gelegt (CONTEXT.md, gemischte Auswahl).
  */
  const handleRestShortcut = useCallback((taskId: string) => {
    const task = tasks?.find(entry => entry.id === taskId)
    if (!task) return
    handleContextMenuBadgeUpdate(taskId, task.pausedAt ? wakeTaskUpdate(task, new Date()) : { pausedAt: new Date() })
  }, [tasks, handleContextMenuBadgeUpdate])

  // ── Keyboard shortcuts & IPC listeners ──
  useProjectKeyboardShortcuts({
    savedName, tasks, creatingPosition, creatingAfterTaskId, segments: visibleSegments,
    activeSegmentRef, navClearSelection, navSelectTask,
    startCreating, startCreatingAfter,
    // ⇧A/⇧B/⇧F und das Menü — derselbe Weg wie der Knopf der Pille.
    onAddSection: handleAddSectionAt,
    handleDuplicate,
    focusedNavIndex: navigation.focusedNavIndex,
    flatNavList: navigation.flatNavList,
    addSectionDisabled: showsSingleSection,
    singleSection: showsSingleSection,
    isActive,
    // ⌘N und Menü wie der Knopf der Pille: immer oben (#272).
    onNewTaskCommand: handleListToolbarAddTask,
  })
  /*
    **Die Kürzel des Aufgaben-Menüs** — sie gelten auf jeder Seite mit einer
    Aufgabenliste, deshalb ein eigener Hook und nicht der Projekt-Hook, dessen
    übrige Tasten Aufgaben ANLEGEN.
  */
  // ⇧⌘F (#210): der Stern der Auswahl, ohne Auswahl der des Projekts — mit Meldung und Rückgängig.
  const favoriteShortcut = useFavoriteShortcut({ getSelection: () => navigation.selectedIds, applyUpdate: handleContextMenuBadgeUpdate })
  const projectFavoriteShortcut = useProjectFavoriteShortcut(project)
  const showTaskInProject = useShowTaskInProject()
  useTaskMenuShortcuts({
    enabled: isActive,
    getAnchorTaskId: () => {
      const focused = navigation.focusedNavIndex !== null
        ? navigation.flatNavList[navigation.focusedNavIndex]
        : null
      return anchorTaskId(
        focused?.type === 'task' ? focused.id : null,
        navigation.flatNavList.filter(item => item.type === 'task').map(item => item.id),
        navigation.selectedIds,
      )
    },
    onMenuShortcut: (taskId, target) => setMenuShortcut({ taskId, target }),
    onFavoriteShortcut: taskId => { void (taskId ? favoriteShortcut(taskId) : projectFavoriteShortcut()) },
    onRestShortcut: handleRestShortcut,
    onFocusShortcut: handlePlayTask,
    onDetailsShortcut: detailsPaneAllowed ? details.toggle : undefined,
    onShowInProjectShortcut: taskId => {
      // Auf der Seite des eigenen Projekts stünde man schon dort (wie im Menü „Aufgaben“).
      if (effectiveTasks?.find(task => task.id === taskId)?.projectId !== projectId) showTaskInProject(taskId)
    },
    canMove: true,
  })

  // **Verschieben …** (#257): Rechtsklick, ⇧⌘M und das Menü „Aufgaben“.
  const moveMenu = useMoveMenu(() => navigation.selectedIds, openMoveAtRow)

  /*
    **Die Befehle der Auswahl in der Pille** (#291): ab zwei markierten Aufgaben Start, Verschieben,
    Löschen und das ⋯ — jeweils nur für die Aufgaben, für die sie gelten.
  */
  const taskUpdates = useTaskUpdates({ setOptimisticTaskOverride, logTag: 'ProjectPage' })
  const listedTaskIds = useMemo(
    () => navigation.flatNavList.filter(item => item.type === 'task').map(item => item.id),
    [navigation.flatNavList],
  )
  const renderSelection = useSelectionActions({
    selectedIds: navigation.selectedIds,
    orderedTaskIds: listedTaskIds,
    tasks: effectiveTasks,
    updates: taskUpdates,
    toggleComplete: toggleCompleteWithGrace,
    deleteTasks: (ids) => { void handleDeleteTask(ids) },
    duplicateTasks: (ids) => { void handleDuplicate(ids) },
    clearSelection: navClearSelection,
    moveMenu,
    nestMenu,
    // Im Projekt verlässt nichts die Liste, wenn ein Datum kommt.
    onWritten: null,
    restoreListFocus: navigation.restoreListFocus,
  })

  /*
    **Das Menü „Aufgaben“ in der Menüleiste** (#257): dieselben Griffe wie die Tasten, dazu
    Erledigen, Löschen, Duplizieren und die Details. Der Anker reaktiv — das Menü muss wissen,
    ob etwas gewählt ist, bevor jemand es aufklappt.
  */
  const menuAnchor = useMemo(() => {
    const focused = navigation.focusedNavIndex !== null ? navigation.flatNavList[navigation.focusedNavIndex] : null
    const id = anchorTaskId(
      focused?.type === 'task' ? focused.id : null,
      navigation.flatNavList.filter(item => item.type === 'task').map(item => item.id),
      navigation.selectedIds,
    )
    return menuAnchorOf(id ? effectiveTasks?.find(task => task.id === id) : null, effectiveTasks)
  }, [navigation.focusedNavIndex, navigation.flatNavList, navigation.selectedIds, effectiveTasks])
  useAppTaskMenu({
    enabled: isActive,
    anchor: menuAnchor,
    multiSelected: navigation.selectedIds.size > 1,
    detailsOpen,
    currentProjectId: projectId,
    canMove: true,
    onMenuShortcut: (taskId, target) => setMenuShortcut({ taskId, target }),
    onComplete: navigation.completeSelection,
    onFocus: handlePlayTask,
    onRest: handleRestShortcut,
    onFavorite: taskId => { void favoriteShortcut(taskId) },
    onToggleDetails: detailsPaneAllowed ? details.toggle : undefined,
    onShowInProject: showTaskInProject,
    onDuplicate: () => handleDuplicate(),
    onDelete: navigation.deleteSelection,
  })


  // While the project loads (or if it truly doesn't exist — Dexie returns
  // undefined for both), render the empty shell. The title input's `name`
  // state starts as '' so the placeholder takes over until data arrives,
  // preventing the skeleton-to-title position jump that was visible on
  // Quick Notes → Project tab switches.
  //
  // `project.id !== projectId` gehört dazu: `useLiveQuery` liefert beim
  // Projektwechsel WEITER die Zeile des vorigen Projekts, bis die neue Abfrage
  // aufgelöst ist (`usePageCrossfade` beschreibt dasselbe für Aufgaben und
  // Abschnitte, dort heißt die Bedingung `correctDataReady`). Ohne diese
  // Prüfung stünden auf der Seite des NEUEN Projekts ein bis drei Bilder lang
  // die Canvas-Größe und die Spaltenbreite des ALTEN — und weil das Panel einen
  // 250ms-Übergang trägt, sähe man die Korrektur als Animation: Der Canvas geht
  // kurz auf und klappt vor den Augen wieder zu.
  if (loading || !project || project.id !== projectId) {
    return (
      <div className="task-view project-page">
        <div className="task-view__header">
          <div className="task-view__title-section" />
        </div>
      </div>
    )
  }

  return (
    <TitleRecognitionScope lifeSpace={lifeSpace} projectId={projectId ?? null}>
    <TaskDetailsOpenerProvider value={details.opener}>
    <MoveMenuContext.Provider value={moveMenu}>
    <div ref={pageRef} className={`task-view project-page${isCompactMode ? ' project-page--list-mode' : ''}${isStackMode ? ' project-page--stack-mode' : ''}${isCanvasFocused ? ' project-page--canvas-open' : ''}${canvasMode === 'maximized' ? ' project-page--canvas-maximized' : ''}${canvasMode === 'peek' ? ' project-page--canvas-peek' : ''}${canvasMode === 'closing' ? ' project-page--canvas-closing' : ''}${detailsOpen && !detailsOverCanvas ? ' project-page--details-open' : ''}${detailsVisible && !detailsOpen && !detailsOverCanvas ? ' project-page--details-closing' : ''}${detailsOverCanvas && detailsOpen ? ' project-page--details-over' : ''}${detailsOverCanvas && !detailsOpen ? ' project-page--details-over-closing' : ''}${showEmptyStateTeaser ? ' project-page--empty' : ''}`}>
      {/* Der Kopf gehört zur Aufgabenliste, nicht zur Seite: Titel, Notizen und
          der Liste/Stapel-Umschalter stehen mit der Liste in derselben Spalte
          und enden an deren rechter Kante. Läge er wie früher über dem ganzen
          Body, spannte er sich bei geöffnetem Canvas auch über diesen hinweg. */}
      <div ref={bodyRef} className="project-page__body">
      {/* Die Verläufe hängen an der SPALTE, nicht am Scroller. Innerhalb des
          Scrollers klemmte sie dessen Inhaltskasten (unten schnitt das
          `padding-bottom` sie 48px vor der Kante ab) und sie mussten sich per
          z-index gegen Aufgaben und Abschnittsköpfe durchsetzen — wobei der
          angeheftete Kopf laut seinem eigenen Kommentar ÜBER dem Verlauf
          liegen soll. Außerhalb gibt es beide Konflikte nicht; genauso lagen
          die alten `.tab-stack__fade` als Geschwister des Scrollers. */}
      <div ref={listColumnRef} className="project-page__list-column">
      <div
        ref={bodyMainRef}
        className={`project-page__body-main${scrollbarVisible ? ' project-page__body-main--scroll-visible' : ''}`}
        onScroll={handleListScroll}
      >
      <div
        ref={headerRowRef}
        className={`task-view__header project-page__header${headerActionsHidden ? ' project-page__header--actions-hidden' : ''}`}
      >
        <div
          ref={headerRef}
          className="task-view__title-section"
          onBlur={handleHeaderBlur}
        >
          <ProjectHeaderTitle
            value={nameHtml}
            placeholder={t('project.defaultName')}
            icon={project.icon}
            iconColor={project.iconColor}
            fieldRef={titleRef}
            iconRowRef={headerIconRowRef}
            onValueChange={(html, plain) => {
              setNameHtml(html)
              setName(plain)
            }}
            onSubmit={handleTitleSubmit}
            onCommit={handleTitleCommit}
            onCancel={handleRevert}
            onArrowDownAtEnd={handleTitleArrowDown}
            onFocus={() => setIsEditingHeader(true)}
            onIconChange={handleIconChange}
          />
          {/* Notes: textarea for multiline, auto-resizes to content.
              Hidden only when empty and not editing (takes no space). */}
          {(isEditingHeader || savedNotes) && (
            <textarea
              ref={notesRef}
              className="project-page__notes-input"
              value={notesValue}
              placeholder={isEditingHeader ? t('project.notesPlaceholder') : undefined}
              onChange={(e) => {
                setNotesValue(e.target.value)
                scheduleAutoResize(e.currentTarget)
              }}
              onKeyDown={handleNotesKeyDown}
              onFocus={() => setIsEditingHeader(true)}
              rows={1}
            />
          )}
        </div>
        {(!showEmptyStateTeaser || detailsAllowed) && (
          <div ref={headerActionsRef} className="project-page__header-actions">
            {/*
              Nicht im leeren Zustand: Liste und Stapel sind zwei Darstellungen
              derselben Aufgabenliste — solange es keine gibt, schaltet der
              Umschalter zwischen zwei Arten von Nichts. Dasselbe Argument wie beim
              zweiten Knopf des Empty States, der ohne Ziel gar nicht erscheint.
            */}
            {/* Seitenhilfe (#323): Der Titel ist ein Schreibfeld über die ganze Breite — das ? steht vorn bei den Knöpfen,
                aber nur mit Beschriftungen: Wird es eng, weicht es zuerst, damit die Knöpfe selbst bleiben. */}
            {headerActionsMode === 'full' && <PageHelpButton page="project" />}
            {/* Der Stern (#210): ganz links, vor Liste | Stapel — auch im leeren Projekt. */}
            {project && (
              <FavoriteToggle
                favorite={project.favoriteOrder != null}
                onFavoriteChange={setProjectFavorite}
                subjectIds={[project.id]}
              />
            )}
            {/* Mit Beschriftung (Sven, 05.10.2026); bei wenig Platz nur die Zeichen (`headerActionsMode`). */}
            {!showEmptyStateTeaser && <WindowModeToggle iconOnly={headerActionsMode !== 'full'} className="project-page__window-mode" />}
            {/* Eigener Knopf, kein drittes Segment: Liste und Stapel schalten den
                Fenster-Modus und federn zurück, die Aufgaben-Details rasten ein.
                Eine Gruppe, in der zwei Knöpfe zurückfedern und einer einrastet,
                lernt niemand. Er steht auch im leeren Projekt: Die Details
                zeigen dort das Projekt (#220). */}
            {detailsAllowed && (
              <DetailsToggle open={detailsOpen} onToggle={details.toggle} />
            )}
          </div>
        )}
      </div>

      {showEmptyStateTeaser ? (
        <CanvasEmptyStateTeaser
          onCreateTask={() => startCreating('top', 0)}
          onCreateSection={() => { void handleAddSection('bottom') }}
          // Ohne Pinnwand (Listen-Modus) keine Notiz und kein Memo.
          onPlaceNote={showCanvasPanel ? handleListToolbarPlaceNote : undefined}
          onPlaceMemo={showCanvasPanel ? handleListToolbarPlaceMemo : undefined}
          onNotePointerDown={showCanvasPanel ? handleListToolbarNotePointerDown : undefined}
          onMemoPointerDown={showCanvasPanel ? handleListToolbarMemoPointerDown : undefined}
        />
      ) : isStackMode ? (
        <TaskStackView
          tasks={stackQueue}
          isActive={isActive}
          initialActiveId={stackEntryTaskId}
          onActiveTaskChange={handleStackActiveTaskChange}
          onToggleComplete={toggleCompleteWithGrace}
          onDelete={handleDeleteTask}
          onDuplicate={handleDuplicateFromContextMenu}
          onContextMenuBadgeUpdate={handleContextMenuBadgeUpdate}
          getContextMenuMarks={getContextMenuMarks}
          menuShortcut={menuShortcut}
          onPlay={handlePlayTask}
          onMenuShortcutClose={closeMenuShortcut}
          onSubmitEdit={handleUpdateTask}
        />
      ) : (
      <TaskBoard
        inProjectContext
        segments={visibleSegments}
        tasks={tasks}
        projectId={projectId}
        gapAfterSegmentIdx={gapAfterSegmentIdx}
        gapHeight={gapHeight}
        activeDragId={activeDragId}
        draggedIds={draggedIds}
        subtaskDragOut={subtaskDragOut}
        insertLine={insertLine}
        nestMenu={nestMenu}
        nestTarget={nestTarget}
        dragCount={dragCount ?? 0}
        isCrossSegmentDrag={isCrossSegmentDrag}
        activeSectionDragId={activeSectionDragId}
        dragSourceSegIdx={dragSourceSegIdx}
        sectionDragWidth={sectionDragWidth}
        dragRotation={dragRotation}
        sectionDragYOffset={sectionDragYOffset}
        taskListRef={taskListRef}
        taskListHandleRef={taskListHandleRef}
        getSegmentHandleRef={getSegmentHandleRef}
        sensors={sensors}
        customCollisionDetection={customCollisionDetection}
        allSortableIds={allSortableIds}
        segmentAwareStrategy={segmentAwareStrategy}
        composedDragStart={composedDragStart}
        composedDragMove={composedDragMove}
        composedDragOver={composedDragOver}
        composedDragEnd={composedDragEnd}
        composedDragCancel={composedDragCancel}
        navigation={navigation}
        hoveredSegmentIndex={hoveredSegmentIndex}
        setHoveredSegmentIndex={setHoveredSegmentIndex}
        creatingSegmentIdx={creatingSegmentIdx}
        creatingPosition={creatingPosition}
        creatingAfterTaskId={creatingAfterTaskId}
        pendingInlineTarget={pendingInlineTarget}
        creationKey={creationKey}
        creationLanded={creationLanded}
        newTaskDrop={newTaskDrag.drop ?? newSectionDrag.drop}
        inlineResetKey={inlineResetKey}
        skipInlineEntrance={skipInlineEntrance}
        autoSelectId={autoSelectId}
        morphData={morphData}
        positionMorphKey={positionMorphKey}
        inlineMorphTitle={inlineMorphTitle}
        inlineMorphSelected={inlineMorphSelected}
        inlineMorphKey={inlineMorphKey}
        topCreateRef={topCreateRef}
        bottomCreateRef={bottomCreateRef}
        handleCreateTask={handleCreateTask}
        handleCreateTaskAndContinue={handleCreateTaskAndContinue}
        handleCreateTaskAfter={handleCreateTaskAfter}
        handleCreateTaskAfterAndContinue={handleCreateTaskAfterAndContinue}
        handleCancelCreate={handleCancelCreate}
        handleMorphComplete={handleMorphComplete}
        handleInlineMorphComplete={handleInlineMorphComplete}
        autoEditSectionId={autoEditSectionId}
        handleSectionNamingChange={handleSectionNamingChange}
        handleToggleCollapse={handleToggleCollapse}
        handleRenameSection={handleRenameSection}
        handleDeleteSection={handleDeleteSection}
        handleArchiveSection={handleArchiveSection}
        handleUnarchiveSection={handleUnarchiveSection}
        archiveReopenCounts={archiveReopenCounts}
        handleToggleComplete={toggleCompleteWithGrace}
        handleDeleteTask={handleDeleteTask}
        handleUpdateTask={handleUpdateTask}
        handleUpdateTaskAndCreateBelow={handleUpdateTaskAndCreateBelow}
        handleDuplicate={handleDuplicateFromContextMenu}
        handleContextMenuBadgeUpdate={handleContextMenuBadgeUpdate}
        getContextMenuMarks={getContextMenuMarks}
        menuShortcut={menuShortcut}
        onPlay={handlePlayTask}
        onMenuShortcutClose={closeMenuShortcut}
        optimisticUpdates={optimisticUpdates}
        handleHoverLeave={handleHoverLeave}
        handleClearSelection={navClearSelection}
        completedToggle={(
          <CompletedToggle
            tasks={completedTasks}
            open={completedOpen}
            stateKey={`project-done-${projectId}`}
            onOpenChange={setCompletedOpen}
            onRestore={handleRestoreCompleted}
            onDelete={taskId => handleDeleteTask(new Set([taskId]))}
            externalSelection={navigation.getExternalSelection(completedTasks.map(task => task.id))}
            onNavigateEdit={navigation.navigateEdit}
            onUpdateTask={handleUpdateTask}
            archivedGroups={archivedGroups}
            renderArchivedSection={section => (
              <SectionHeader
                id={section.id}
                title={section.title}
                taskCount={0}
                collapsed={false}
                archived
                showWindowModeToggle={false}
                onToggleCollapse={noopSection}
                onRename={handleRenameSection}
                onDelete={handleDeleteSection}
                onUnarchive={handleUnarchiveSection}
                reopenCount={archiveReopenCounts.get(section.id) ?? 0}
              />
            )}
          />
        )}
      />
      )}
      </div>
      {/*
        Schwebt über dem Scroller, nicht in ihm: Sie bleibt stehen, während die
        Liste darunter läuft. Nicht im leeren Zustand (dort steht sie im
        Teaser), nicht im Stapel und nicht, solange das Projekt noch keinen
        Namen hat — wie zuvor das Plus.
      */}
      {!showEmptyStateTeaser && !isStackMode && savedName.trim() && (
        <ListToolbarDock
          isActive={isActive}
          columnRef={listColumnRef}
          canvasAvailable={showCanvasPanel}
          canvasOpen={canvasIsOpen}
          sectionsAllowed={!showsSingleSection}
          retracted={listToolbarRetracted}
          onAddTask={handleListToolbarAddTaskClick}
          onAddSection={handleListToolbarAddSection}
          onPlaceNote={handleListToolbarPlaceNote}
          onPlaceMemo={handleListToolbarPlaceMemo}
          onNotePointerDown={handleListToolbarNotePointerDown}
          onMemoPointerDown={handleListToolbarMemoPointerDown}
          onAddTaskPointerDown={newTaskDrag.onAddTaskPointerDown}
          addTaskDragging={newTaskDrag.dragging}
          onAddSectionPointerDown={newSectionDrag.onAddTaskPointerDown}
          addSectionDragging={newSectionDrag.dragging}
          renderSelection={renderSelection}
        />
      )}
      {newTaskDrag.ghost && <NewTaskDragGhost {...newTaskDrag.ghost} />}
      {newSectionDrag.ghost && <NewTaskDragGhost {...newSectionDrag.ghost} />}
      </div>
      {/*
        Geschwister der Listenspalte: Als **Spalte** rückt die Liste zusammen,
        als **Slide-over** verlassen sie den Fluss und legen sich linksbündig
        über den Canvas — `project-page__body` ist dafür der Bezugsrahmen.

        Ohne ausgewählte Aufgabe stehen sie trotzdem da und zeigen ihre leere
        Zeile — zu gehen sie nur über das Kreuz und den Knopf. Das ist kein
        Sonderfall, sondern der Regelfall beim Durchlaufen: Ein Abschnittskopf
        hebt die Aufgaben-Auswahl auf, und die nächste Zeile füllt sie wieder.

        Nach dem Löschen rückt die Liste ohnehin von selbst nach
        (`useProjectNavigation`) — die Aufgaben-Details erben das, statt eigenes
        zu rechnen.
      */}
      {detailsVisible && (
        <TaskDetails
          tasks={details.tasks}
          openSubtask={details.subtaskRequest}
          openComments={details.commentsRequest}
          openReminders={details.remindersRequest}
          project={details.project}
          onMovedToLifeSpace={handleMovedToLifeSpace}
          focusedSeconds={details.focusedSeconds}
          onUpdate={handleContextMenuBadgeUpdate}
          onClose={details.close}
          onStepTask={details.stepTask}
          onRest={details.onRest}
          canStepPrev={details.canStepPrev}
          canStepNext={details.canStepNext}
          // Escape darin gibt die Tastatur an die Liste zurück und schließt
          // NICHT: Der Ausgang ist das Kreuz (CONTEXT.md).
          onReleaseFocus={details.releaseFocusToList}
          focusTitleSignal={details.focusTitleSignal}
        />
      )}
      {showCanvasPanel && projectId && (
        /*
          Der Schlüssel ist kein Zierrat: Ein Klick auf ein Projekt in der
          Seitenleiste öffnet KEINEN neuen Tab, sondern schreibt den Pfad des
          aktiven um (`navigateActiveTab`); `TabStack` keyt nach `tab.id`, diese
          Seite bleibt also stehen und bekommt bloß eine neue `projectId`. Ohne
          den Schlüssel behielte das Panel den Ausschnitt des vorigen Projekts
          (React Flow liest `defaultViewport` nur beim Aufbau) und dazu sein
          scharfgestelltes Werkzeug — der nächste Klick auf die Fläche setzte
          dann eine Notiz im falschen Projekt ab.
        */
        <ProjectCanvasPanel
          key={projectId}
          projectId={projectId}
          state={canvasPanelState}
          onStateChange={changeCanvasPanelState}
          splitRatio={pendingSplitRatio !== undefined ? pendingSplitRatio : projectSplitRatio}
          onSplitRatioCommit={handleSplitRatioCommit}
          /*
            Die **Werkzeug-Pille** bleibt in zwei Fällen weg. Erstens solange
            der Teaser steht — neben einer Einladung soll keine Bedienung
            schweben. Zweitens bei offenen Aufgaben-Details: Sie nehmen die
            halbe Fläche, und die Pille schwebt am rechten Rand, also über
            ihnen. Das **Drag Handle** bleibt in beiden Fällen, es ruht
            unsichtbar an der Kante (ADR 0010).
          */
          showToolbar={!showEmptyStateTeaser && !detailsVisible}
          /*
            Der **Canvas-Anriss** weicht den Aufgaben-Details. Sie nehmen die
            halbe Fläche und damit genau den Platz, an dem der Streifen sonst
            liegt. Die Pille bleibt — sie ist der Mausweg zum Canvas, und die
            Rangfolge „Wer den Canvas öffnet, schließt die Aufgaben-Details"
            setzt ihn voraus.
          */
          peekSuppressed={detailsVisible}
          /*
            Das **Drag Handle** fällt nur dann weg, wenn die Karte wirklich über
            der Pinnwand liegt. Steht sie neben einer GESCHLOSSENEN, bleibt er —
            dort ist er der Mausweg in den Canvas hinein, und die Rangfolge „Wer
            den Canvas öffnet, schließt die Aufgaben-Details" setzt ihn voraus.
          */
          coveredByDetails={detailsOverCanvas}
          openTarget={canvasOpenTarget}
          placementRequest={canvasPlacementRequest}
          onPlacingChange={handleCanvasPlacingChange}
          onApplyNotes={applyCanvasNotes}
          onOpenTask={revealTaskInList}
          revealRequest={canvasReveal}
          projectName={project?.name}
          projectNameHtml={project?.nameHtml}
          projectIcon={project?.icon}
          projectIconColor={project?.iconColor}
        />
      )}
      </div>
      {/*
        Schein am unteren Rand, über die ganze Tab-Breite — nur im leeren
        Projekt. Er beginnt unter dem Primär-Knopf (Anker `--empty-state-primary`,
        EmptyStateGlow.scss) und steht deshalb HINTER ihnen im
        Baum: Ein Anker muss vor dem verankerten Element kommen.
      */}
      {showEmptyStateTeaser && <EmptyStateGlow className="project-page__glow" />}
      <ArchiveSectionDialog
        open={pendingArchive !== null}
        onOpenChange={open => { if (!open) cancelArchive() }}
        title={pendingArchive?.title ?? ''}
        openCount={pendingArchive?.openCount ?? 0}
        onConfirm={confirmArchive}
      />
    </div>
    </MoveMenuContext.Provider>
    </TaskDetailsOpenerProvider>
    </TitleRecognitionScope>
  )
}
